import { StreakSessions } from '~/team_tools/features/player/components/StreakSessions'
import {
    faBan,
    faCheckCircle,
    faCheckToSlot,
    faArrowUpRightFromSquare,
    faCircleInfo,
    faFaceDizzy,
    faFaceMeh,
    faFaceSmile,
    faXmark,
    faChevronDown,
    faChevronRight,
    faClockRotateLeft,
    faEye,
    faSpinner,
    faStopwatch,
    faUser,
    faWarning,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { createContext, Fragment, useContext, useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { toast } from 'react-toastify'
import { twMerge } from 'tailwind-merge'
import { PlayPanel } from '~/team_tools/features/player/components/PlayPanel'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { FraudDetail } from './FraudDetail'
import {
    useWithdrawCheckFinalized,
    useWithdrawDelete,
    useWithdrawProcess,
    useWithdrawReturnFailed,
    useWithdrawRevokeApprove,
} from '../api'
import { useWithdrawApprove } from '../api/useWithdrawApprove'
import { useWithdrawDecline } from '../api/useWithdrawDecline'
import { useWithdrawDeclineHold } from '../api/useWithdrawDeclineHold'
import { useWithdrawVerdict } from '../api/useWithdrawVerdicts'
import { useForgiveFlag, useUnforgiveFlag } from '../api/useForgiveFlag'
import { WithdrawRequest as WithdrawRequestType } from '../types'
import { formatWalletAddress } from '../utils'
import { currencies } from './SelectCurrency'
import { WithdrawRequestListContext } from './WithdrawRequestList'
import { Copyable } from '~/components/ui/Copyable'
import { INTEGRITY_MEANING, integrityAge, integrityBasisText, isFewMatches } from '~/team_tools/integrityCopy'
import { showInZone, useZone } from '~/team_tools/components/timezone'

/** Staff times in the Team Tools zone picker's zone, with the zone named.
 *  Every time on the staff side of this screen was the browser's own
 *  `toLocaleString()`, so two reviewers in two countries read the same payout
 *  at two different hours and neither was told which (B-c37). The player's
 *  own view keeps their browser's time: the picker is a staff setting. */
const useStaffTime = () => {
    const [zone] = useZone()
    return (iso?: string | null, named = true) =>
        iso ? (named ? `${showInZone(iso, zone)} (${zone})` : showInZone(iso, zone)) : '?'
}

type WithdrawRequestContextType = {
    withdrawRequest: WithdrawRequestType
    details: boolean
    showDetails: React.Dispatch<React.SetStateAction<boolean>>
    /** The per-row verdict request failed. The row stays usable without it. */
    verdictFailed?: boolean
}

const WithdrawRequestContext = createContext<WithdrawRequestContextType>({
    withdrawRequest: undefined,
    details: false,
    showDetails: () => {},
})

type WithdrawRequestProviderProps = {
    withdrawRequest: WithdrawRequestType
    /** Fetch this row's verdict when it is expanded, instead of expecting the
     *  list to have sent it. The staff queue sets it; see useWithdrawVerdict. */
    lazyVerdict?: boolean
    /** Start with the case open, for a page that opens one request on its
     *  own (the account page). */
    open?: boolean
    /** Called when that case is closed, so the opener can drop it. */
    onClose?: () => void
    children: React.ReactNode
}

const WithdrawRequest = ({
    withdrawRequest: row,
    lazyVerdict = false,
    open = false,
    onClose,
    children,
}: WithdrawRequestProviderProps) => {
    const [details, setDetails] = useState(open)
    const showDetails = (next: React.SetStateAction<boolean>) => {
        const value = typeof next === 'function' ? next(details) : next
        if (!value && onClose) onClose()
        else setDetails(value)
    }

    // Only when opened, and only when the list did not already send a verdict
    // (the verdict filter computes them server-side, and asking again would pay
    // for the same triage twice).
    //
    // `== null`, not `=== undefined`. The list sends `triage: null` for a row
    // it left the verdict off, and the strict check read that as "already
    // sent", so no row ever fetched one: found on dev 2026-09-28, with the
    // Approve button unaware of any block (the server still refuses one).
    const wantVerdict = lazyVerdict && row.triage == null
    const verdict = useWithdrawVerdict(row.uuid, wantVerdict && details)
    const loaded = wantVerdict ? verdict.data?.[row.uuid] : undefined

    let withdrawRequest = row
    if (loaded) {
        withdrawRequest = {
            ...row,
            triage: loaded.triage,
            payout_block: loaded.payout_block,
            checks_failed: loaded.checks_failed,
        }
    } else if (wantVerdict && details && !verdict.isError) {
        withdrawRequest = { ...row, verdict_pending: true }
    }

    return (
        <WithdrawRequestContext.Provider
            value={{
                withdrawRequest,
                details,
                showDetails,
                verdictFailed: (wantVerdict && verdict.isError) || !!withdrawRequest.checks_failed,
            }}
        >
            {children}
        </WithdrawRequestContext.Provider>
    )
}

const WithdrawRequestStatus = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const status = withdrawRequest?.status

    const textColor = {
        requested: 'text-white',
        approved: 'text-blue-500',
        processing: 'text-yellow-500',
        finalized: 'text-green-500',
        declined: 'text-red-500',
    }

    const statusIcon = {
        requested: faEye,
        approved: faCheckToSlot,
        processing: faStopwatch,
        finalized: faCheckCircle,
        declined: faWarning,
    }

    return (
        <small className={twMerge('flex flex-row gap-x-1 items-center uppercase', textColor[status])}>
            <span>{status}</span>
            <FontAwesomeIcon icon={statusIcon[status]} />
        </small>
    )
}

const WithdrawRequestStatusForUser = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const status = {
        requested: 'submitted',
        approved: 'processing',
        processing: 'processing',
        finalized: 'completed',
        declined: 'declined',
    }[withdrawRequest?.status]

    const textColor = {
        submitted: 'text-white',
        processing: 'text-yellow-500',
        completed: 'text-green-500',
        declined: 'text-red-500',
    }

    const statusIcon = {
        submitted: faEye,
        processing: faStopwatch,
        completed: faCheckCircle,
        declined: faWarning,
    }

    return (
        <small className={twMerge('flex flex-row gap-x-1 items-center uppercase', textColor[status])}>
            <span>{status}</span>
            <FontAwesomeIcon icon={statusIcon[status]} />
        </small>
    )
}

const WithdrawRequestAmount = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)

    // A currency the icon list does not carry rendered <img src={undefined}>,
    // which the browser draws as a broken-image box beside the amount. On a
    // payments row that empty square reads as a control, and somebody asks
    // what it does. Name it instead, which is also the more useful fallback.
    const icon = currencies.find((c) => c.name == withdrawRequest.currency)?.img

    return (
        <div className='flex flex-row items-center gap-1'>
            <div>{withdrawRequest.amount}</div>
            {icon ? (
                <img src={icon} alt={withdrawRequest.currency} className='h-4 w-4' />
            ) : (
                <span className='text-white/40'>{withdrawRequest.currency}</span>
            )}
        </div>
    )
}

const WithdrawRequestDetailsButton = ({ children }) => {
    const { showDetails } = useContext(WithdrawRequestContext)
    return <button onClick={() => showDetails((prev) => !prev)}>{children}</button>
}

const WithdrawRequestDetailsContainer = ({ children }) => {
    const { details } = useContext(WithdrawRequestContext)
    return details && children
}

const WithdrawRequestDetails = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const tx_date = withdrawRequest.status_history.find((t) => t.status === 'processing')?.created_at
    const tx = withdrawRequest.blockchain_tx
    return (
        <div className='grid grid-cols-[max-content,auto] gap-1 p-2'>
            <div className='text-white/60'>Id:</div>
            <div> {withdrawRequest.uuid}</div>
            <div className='text-white/60'>To:</div>
            <div> {formatWalletAddress(withdrawRequest.wallet)}</div>
            {tx_date && (
                // Labelled: an unlabelled time under the address read as when
                // the money arrived. It is when the payout was started.
                <div className='col-span-2 text-white/60 mt-2'>Payout started {new Date(tx_date).toLocaleString()}</div>
            )}
            {tx && (
                <>
                    <div className='text-white/50'>Tx:</div>
                    <a
                        target='_blank'
                        href={withdrawRequest.blockchain_tx_url}
                        className={twMerge(
                            'hover:underline underline-offset-2 w-max',
                            { processing: 'text-yellow-500', finalized: 'text-green-500' }[withdrawRequest.status]
                        )}
                    >
                        {formatWalletAddress(withdrawRequest.blockchain_tx)}
                    </a>
                </>
            )}
        </div>
    )
}

const WithdrawRequestDeleteButton = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const { mutate, error, isLoading } = useWithdrawDelete()
    const remove = () => {
        mutate({ uuid: withdrawRequest.uuid })
    }
    return (
        <button
            onClick={remove}
            className='flex flex-row gap-1 items-center p-1 m-1 border border-white/10 rounded hover:border-white/50 underline-offset-2 w-max'
        >
            <div className='text-red-500 uppercase font-bold'>Delete</div>
            {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
        </button>
    )
}

const WithdrawRequestApproveButton = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const { mutate, isLoading } = useWithdrawApprove()
    const [comment, setComment] = useState<string>('')

    const [overriding, setOverriding] = useState(false)

    const block = withdrawRequest.payout_block
    const blocked = !!block?.blocked
    // A freeze and an integrity-gate hold refuse on the server with no
    // override, so the button says so instead of failing after the click.
    // From `account_flags`, which arrives with the row and does not depend on
    // the triage being switched on (PAY-26, B-c21).
    const frozen = withdrawRequest.account_flags?.frozen
    const gateHold = withdrawRequest.account_flags?.gate_hold
    // A ban or a pause in force refuses on the server too (D-039).
    const ban = withdrawRequest.account_flags?.ban
    const held = frozen
        ? `Funds frozen${frozen.reason ? `: ${frozen.reason}` : ''}. Release the freeze before approving.`
        : gateHold
          ? `Held by the integrity gate (${gateHold.reason}). Clear it on the Anti Cheat screen or add an exemption before approving.`
          : ban
            ? `This account is ${ban.kind === 'pause' ? 'paused' : 'banned'}${ban.reason ? ` (${ban.reason})` : ''}. Lift it or decline the request; it cannot be approved while it is in force.`
            : null
    // Not known yet is not "clear". The pill arrives with the verdict, a second
    // after the row, and approving in that second would skip the refusal the
    // reviewer is about to be shown. The server would still refuse it.
    const pending = !!withdrawRequest.verdict_pending

    const approve = (overrideReason?: string) => {
        mutate(
            { uuid: withdrawRequest.uuid, comment, override_reason: overrideReason },
            {
                onSuccess: () => {
                    setOverriding(false)
                    toast.success(overrideReason ? 'Paid over the block' : 'Request approved')
                },
                onError: (error) => toast.error(error.error.message),
            }
        )
    }

    /* The override, and it is deliberately not one click.
     *
     * Every other control here that costs somebody money -- ban, freeze, decline
     * -- asks for a written reason. This is the one that pays an account the
     * checks say should not be paid, so it asks for the same thing. The server
     * refuses without it rather than trusting the page to have collected it, and
     * the reason lands on the status-history row, which is what somebody reads
     * months later when they ask why this was paid. */
    if (overriding && block) {
        return (
            <div className='p-2 w-full'>
                <ConfirmBar
                    message={`Pay ${withdrawRequest.amount} ${withdrawRequest.currency} to ${withdrawRequest.created_by} anyway?`}
                    detail={blockWarning(block.reasons)}
                    confirmLabel='Override and pay'
                    note='Why this should be paid despite the above'
                    noteRequired
                    // payout_block.MIN_OVERRIDE_REASON on the server (PAY-75).
                    noteMinLength={10}
                    notePlaceholder='Recorded against this payout, with your name and the date.'
                    busy={isLoading}
                    onConfirm={(_choice, note) => approve(note)}
                    onCancel={() => setOverriding(false)}
                />
            </div>
        )
    }

    /* ONE PROMPT, NOT TWO.
     *
     * There was a note box beside the button and then a confirmation that
     * asked for a note again, in almost the same words -- so a reviewer typed
     * their reason, pressed Approve, and was asked for their reason. Two
     * fields that look like the same question are not a safeguard; the second
     * one is just read as a mistake and clicked through, which is the opposite
     * of what a confirmation is for.
     *
     * So a routine row is: type the note if you want one, press Approve, done.
     *
     * The ceremony stays exactly where it earns its place -- the override
     * below, which pays an account the checks say should not be paid. That one
     * demands a written reason and the SERVER refuses without it, so it is not
     * a prompt the page could drop even if it wanted to.
     */

    return (
        <div className='flex flex-col gap-1 p-2 w-full'>
            <div className='flex flex-row gap-2 w-full'>
                <button
                    onClick={() => approve()}
                    disabled={!!held || blocked || pending || isLoading}
                    title={
                        held
                            ? held
                            : pending
                              ? 'Still checking this withdrawal'
                              : blocked
                                ? blockWarning(block.reasons)
                                : 'Sends the money. It cannot be taken back once it has been paid out.'
                    }
                    className='flex w-max flex-row items-center gap-2 rounded-md border border-green-500/40 bg-green-500/10 px-4 py-1.5 font-bold uppercase tracking-wide text-green-400 hover:border-green-500/70 hover:bg-green-500/20 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-green-500/10'
                >
                    Approve
                    {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                </button>
                <input
                    type='text'
                    className='flex-1 rounded-md border border-white/15 bg-white/[0.03] px-3 text-white outline-none placeholder:text-white/25 focus:border-white/40'
                    placeholder='Note for the record (optional)'
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </div>
            {held && <span className='px-1 text-red-400 font-normal'>{held}</span>}
            {blocked && !held && (
                <div className='flex flex-row flex-wrap items-center gap-2 px-1'>
                    {/* The refusal in full, not just the pill. A pill has to fit
                        on a row; this is where the reviewer finds out what they
                        would be overriding. */}
                    <span className='text-red-400 font-normal'>{blockWarning(block.reasons)}</span>
                    <button
                        onClick={() => setOverriding(true)}
                        className='uppercase tracking-wide text-white/50 border border-white/20 rounded px-2 py-0.5 hover:text-white hover:border-white/40'
                    >
                        Override and pay anyway
                    </button>
                </div>
            )}
        </div>
    )
}

const WithdrawRequestDeclineButton = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const decline = useWithdrawDecline()
    const declineHold = useWithdrawDeclineHold()
    const [comment, setComment] = useState<string>('')
    const [confirming, setConfirming] = useState(false)
    const [error, setError] = useState<string>('')

    const busy = decline.isLoading || declineHold.isLoading

    // Declining used to refund silently. The refund is the part worth deciding
    // on -- returned balance is spendable in the store, so handing it back to
    // an account you just refused to pay is a decision, not a default. Both
    // outcomes are named on screen and neither is preselected as "the safe one".
    const submit = (choice?: string) => {
        const mutation = choice === 'hold' ? declineHold : decline
        mutation.mutate(
            { uuid: withdrawRequest.uuid, comment },
            {
                onSuccess: (result: any) => {
                    setConfirming(false)
                    setComment('')
                    // The cascade is named in the toast rather than left to be
                    // noticed. Money moved on rows nobody clicked, and a
                    // reviewer who is not told goes looking for requests that
                    // are already gone -- or worse, assumes they are still
                    // queued and waits for somebody else to handle them.
                    const also = result?.also_declined?.length ?? 0
                    const outcome =
                        choice === 'hold' ? 'Declined, funds frozen' : 'Declined and refunded'
                    toast.success(
                        also > 0
                            ? `${outcome}. ${also} other queued request${
                                  also === 1 ? '' : 's'
                              } from this player declined too.`
                            : outcome
                    )
                },
                onError: (err) => toast.error(err.error.message),
            }
        )
    }

    const open = () => {
        // decline_hold requires a comment server-side, and frozen money with
        // no recorded reason is the kind of thing nobody can explain later.
        if (!comment.trim()) {
            setError('Enter a reason first')
            return
        }
        setError('')
        setConfirming(true)
    }

    if (confirming) {
        return (
            <div className='p-2 w-full'>
                <ConfirmBar
                    // `token_amount` is the debit a refund gives back or a hold
                    // keeps; `amount` is the post-fee payout. Naming only the
                    // payout said 98.5 for a decline that moved 100.
                    message={
                        `Decline ${withdrawRequest.amount} ${withdrawRequest.currency} from ${withdrawRequest.created_by}?` +
                        (withdrawRequest.token_amount != null
                            ? ` ${withdrawRequest.token_amount} USDC is refunded or frozen.`
                            : '')
                    }
                    // Said before the click, not discovered after it. The count
                    // is not known here without a query per row, so the
                    // behaviour is stated and the actual number is reported in
                    // the toast -- which is the honest split between what this
                    // screen knows and what the server does.
                    detail={
                        `Reason: ${comment}` +
                        `
Any other queued requests from ${withdrawRequest.created_by} will be ` +
                        `declined the same way. Requests already approved or paying are left alone.`
                    }
                    confirmLabel='Decline'
                    options={[
                        { value: 'refund', label: 'Refund the balance (they can spend it in the store)' },
                        { value: 'hold', label: 'Freeze the funds (neither paid out nor returned)', danger: true },
                    ]}
                    defaultOption='refund'
                    busy={busy}
                    onConfirm={submit}
                    onCancel={() => setConfirming(false)}
                />
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-1 p-2 w-full'>
            <div className='flex flex-row gap-2 w-full'>
                <button
                    onClick={open}
                    disabled={busy}
                    className='flex w-max flex-row items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-1.5 font-bold uppercase tracking-wide text-red-400 hover:border-red-500/70 hover:bg-red-500/20 disabled:opacity-30'
                >
                    Decline
                    {busy && <FontAwesomeIcon icon={faSpinner} spin />}
                </button>
                <input
                    type='text'
                    className='flex-1 rounded-md border border-white/15 bg-white/[0.03] px-3 text-white outline-none placeholder:text-white/25 focus:border-white/40'
                    placeholder='Decline reason'
                    value={comment}
                    onChange={(e) => {
                        setComment(e.target.value)
                        if (error) setError('')
                    }}
                />
            </div>
            {error && <span className='text-red-500 px-1'>{error}</span>}
        </div>
    )
}

const WithdrawRequestRevokeApproveButton = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const { mutate, isLoading } = useWithdrawRevokeApprove()
    const revoke = () => {
        mutate(
            { uuid: withdrawRequest.uuid },
            { onSuccess: () => toast.info('Request revoked'), onError: (error) => toast.error(error.error.message) }
        )
    }
    return (
        <button
            onClick={revoke}
            className='flex flex-row gap-1 items-center p-1 m-1 border border-white/20 rounded hover:border-white/50 underline-offset-2 w-max'
        >
            <div className='text-blue-500 uppercase font-bold'>Revoke</div>
            {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
        </button>
    )
}

/* A payout that hit an error stays "processing", and no other button takes a
 * processing row. The server asks the chain what became of every transfer:
 * one that landed marks the request paid, ones that all failed put it back in
 * the queue so Decline (refund) or Approve work on it again, and anything it
 * cannot prove is refused with the reason. */
const WithdrawRequestReturnFailedButton = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const { mutate, isLoading } = useWithdrawReturnFailed()
    const [confirming, setConfirming] = useState(false)

    const release = () => {
        mutate(
            { uuid: withdrawRequest.uuid },
            {
                onSuccess: (result) => {
                    setConfirming(false)
                    if (result.status === 'finalized') {
                        toast.success('The transfer landed on chain: marked as paid. Do not refund it.')
                    } else {
                        toast.info('Returned to the queue. Decline it to refund, or approve to pay again.')
                    }
                },
                onError: (error) => toast.error(error.error.message),
            }
        )
    }

    if (confirming) {
        return (
            <div className='p-2 w-full'>
                <ConfirmBar
                    message={`Check this stuck payout to ${withdrawRequest.created_by} on chain?`}
                    detail='If the transfer landed it is marked paid. If it failed it goes back to the queue to decline or approve. Nothing is refunded or paid by this step.'
                    confirmLabel='Check and resolve'
                    busy={isLoading}
                    onConfirm={release}
                    onCancel={() => setConfirming(false)}
                />
            </div>
        )
    }
    return (
        <button
            onClick={() => setConfirming(true)}
            className='flex flex-row gap-1 items-center p-1 m-1 border border-white/20 rounded hover:border-white/50 underline-offset-2 w-max'
        >
            <div className='text-red-400 uppercase font-bold'>Resolve stuck payout</div>
            {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
        </button>
    )
}

const WithdrawRequestProcessButton = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const { mutate, isLoading } = useWithdrawProcess()

    const approve = () => {
        mutate(
            { uuid: withdrawRequest.uuid },
            {
                onSuccess: () => toast.success('Request processed'),
                onError: (error) => toast.error(error.error.message),
            }
        )
    }
    // The same two holds Approve shows. Pay now refuses on both server-side;
    // the payout block is NOT mirrored here, because an approval may already
    // have overridden it and the server is the one that knows which codes.
    const flags = withdrawRequest.account_flags
    const held = flags?.frozen
        ? 'Funds frozen. Release the freeze before paying.'
        : flags?.gate_hold
          ? `Held by the integrity gate (${flags.gate_hold.reason}).`
          : flags?.ban
            ? `This account is ${flags.ban.kind === 'pause' ? 'paused' : 'banned'}. It is not paid while that is in force.`
            : undefined
    return (
        <button
            onClick={approve}
            disabled={!!held || isLoading}
            title={held}
            className='flex flex-row gap-1 items-center p-1 m-1 border border-white/10 rounded hover:border-white/50 underline-offset-2 w-max disabled:cursor-not-allowed disabled:opacity-30'
        >
            <div className='text-yellow-500 uppercase font-bold'>Pay now</div>
            {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
        </button>
    )
}

const WithdrawRequestCheckFinalizedButton = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const { mutate, isLoading } = useWithdrawCheckFinalized()

    const approve = () => {
        mutate(
            { uuid: withdrawRequest.uuid },
            {
                onSuccess: () => toast.success('Request finalized'),
                onError: (error) => toast.error(error.error.message),
            }
        )
    }
    return (
        <button
            onClick={approve}
            className='flex flex-row gap-1 items-center p-1 m-1 border border-white/10 rounded hover:border-white/50 underline-offset-2 w-max'
        >
            <div className='text-green-500 uppercase font-bold'>Check finalized</div>
            {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
        </button>
    )
}

const WithdrawRequestUserTemplate = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const deleteable = withdrawRequest.status === 'requested'
    return (
        <div className='flex flex-col font-bold tracking-wide shadow-sm'>
            <small className='pb-1 font-normal text-white/60'>
                {new Date(withdrawRequest.created_at).toLocaleString()}
            </small>
            <div className='flex flex-col border border-white/10'>
                <WithdrawRequest.DetailsButton>
                    <div className='flex flex-row justify-between bg-white/5 hover:bg-white/10 p-1'>
                        <WithdrawRequest.StatusForUser />
                        <WithdrawRequest.Amount />
                    </div>
                </WithdrawRequest.DetailsButton>
                <WithdrawRequest.DetailsContainer>
                    <small>
                        <WithdrawRequest.Details />
                        {deleteable && <WithdrawRequest.DeleteButton />}
                    </small>
                </WithdrawRequest.DetailsContainer>
            </div>
        </div>
    )
}

/**
 * The three buckets, and what they are called on screen.
 *
 * The labels are not the keys. `cleared / review / hold` reads as
 * innocent / suspicious / guilty, and staff were hearing the middle one as an
 * accusation -- which mattered because, before the checks behind it were
 * recalibrated, most of the queue sat in it. What a reviewer needs from a
 * badge is an instruction they can act on and a phrase they could paste to a
 * player, so the badge says what to do and the key stays put for the API,
 * the sort order and every existing test.
 */
/** An age in the unit somebody would say it in.
 *
 *  "412 days old" is a number to be converted before it means anything; what
 *  a reviewer weighs is "about a year". Days survive under two months, where
 *  the exact count still carries weight.
 */
/** Money, marked as money. A bare "187.62" beside "92" and "9" reads as
 *  another count until you have worked out which column you are in. */
/** A dollar figure to the cent. Totals added up in the browser come out as
 *  36.146999999…, which printed in full and ran out of the chart. */
const money = (v: string | number | null | undefined) => {
    if (v === null || v === undefined || v === '') return '—'
    const n = Number(v)
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : `$${v}`
}

const ageInWords = (days: number | null) => {
    if (days === null || days === undefined) return 'unknown'
    if (days < 60) return `${days} day${days === 1 ? '' : 's'} old`
    if (days < 730) {
        const months = Math.round(days / 30.44)
        return `${months} month${months === 1 ? '' : 's'} old`
    }
    const years = Math.floor(days / 365.25)
    const months = Math.round((days - years * 365.25) / 30.44)
    return months > 0 ? `${years}y ${months}m old` : `${years} year${years === 1 ? '' : 's'} old`
}

/**
 * The verdict as a face, and the card's own outline.
 *
 * A row of pills all the same shape made the queue something to read rather
 * than something to scan: three words in three colours, and the eye had to
 * stop at each one. A face is read before it is read -- you know a row is
 * fine or not from the shape at the left of the name, and the outline says
 * the same thing again at the size of the whole card.
 *
 * The words are still there on hover, and the colour is never the only
 * carrier: the shape of the face differs too, which is what makes this safe
 * for somebody who cannot tell the green from the red.
 */
const VERDICT_FACE = {
    cleared: {
        icon: faFaceSmile,
        text: 'text-green-500',
        card: 'border-green-500/40',
        title: 'Looks routine',
    },
    review: {
        icon: faFaceMeh,
        text: 'text-amber-400',
        card: 'border-amber-400/50',
        title: 'Needs review',
    },
    hold: {
        icon: faFaceDizzy,
        text: 'text-red-500',
        card: 'border-red-500/60',
        title: "Don't pay yet",
    },
}

const VERDICT_STYLE = {
    cleared: {
        label: 'looks routine',
        text: 'text-green-500',
        border: 'border-green-500/40',
        icon: faCheckCircle,
        title: 'Nothing on this row needed digging into. Still your decision to pay.',
    },
    review: {
        label: 'needs review',
        text: 'text-yellow-500',
        border: 'border-yellow-500/40',
        icon: faEye,
        title: 'One or more checks want a look before you decide. Not an accusation.',
    },
    hold: {
        label: "don't pay yet",
        text: 'text-red-500',
        border: 'border-red-500/40',
        icon: faWarning,
        title: 'Something here should be resolved before this money leaves.',
    },
}

/** What is already true about this account, beside its name.
 *
 *  A ban and a staff tag are decisions somebody has already made, not readings
 *  this page computed, so they sit next to the username rather than among the
 *  triage signals -- a reviewer should know the account is banned before they
 *  start weighing what the ledger says about it.
 *
 *  Staff-only: `account_flags` is absent unless the caller asked for staff
 *  detail, so the player-facing list cannot render the internal tags on an
 *  account back to its owner.
 */
const WithdrawRequestAccountFlags = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const flags = withdrawRequest.account_flags
    if (!flags) return null

    const ban = flags.ban
    const tags: string[] = flags.tags ?? []
    const frozen = flags.frozen
    const gateHold = flags.gate_hold
    if (!ban && tags.length === 0 && !frozen && !gateHold) return null

    // A pause is "not sure yet", not a decision, and read as "banned" it told
    // the reviewer somebody had already concluded (PAY-16).
    const paused = ban?.kind === 'pause'

    return (
        <>
            {ban && (
                <span
                    className='uppercase text-[10px] border border-red-500/40 text-red-500 rounded px-1'
                    title={[
                        paused ? 'Paused, pending review' : ban.permanent ? 'Permanent ban' : 'Temporary ban',
                        ban.reason,
                    ]
                        .filter(Boolean)
                        .join(' — ')}
                >
                    {paused ? 'paused' : ban.permanent ? 'banned' : 'banned (temp)'}
                </span>
            )}
            {frozen && (
                <span
                    className='uppercase text-[10px] border border-red-500/40 text-red-500 rounded px-1'
                    title={['Funds frozen', frozen.reason].filter(Boolean).join(' — ')}
                >
                    frozen
                </span>
            )}
            {gateHold && (
                <span
                    className='uppercase text-[10px] border border-red-500/40 text-red-500 rounded px-1'
                    title={`Held by the integrity gate — ${gateHold.reason}`}
                >
                    gate hold
                </span>
            )}
            {tags.map((tag) => (
                <span
                    key={tag}
                    className='uppercase text-[10px] border border-amber-400/40 text-amber-400 rounded px-1'
                    title={flags.tag_by?.[tag] ? `Tagged by ${flags.tag_by[tag]}` : 'Tagged by staff'}
                >
                    {tag}
                </span>
            ))}
        </>
    )
}

/**
 * What each disqualifying check is called on screen.
 *
 * Short enough for a pill and phrased as the finding rather than the mechanism:
 * a reviewer scanning a queue needs "multi-account", not "same_ip_kills".
 */
const BLOCK_LABEL: Record<string, string> = {
    cheat_history: 'confirmed cheat',
    cluster: 'kill farm',
    same_ip_kills: 'multi-account',
    // Named apart from the address version, because a reviewer scanning the
    // queue has to know WHICH reading refused the row: an account behind a VPN
    // shows one of these and not the other, and "multi-account" twice would
    // hide that the address check saw nothing at all.
    // "client-reported" (D-104): the machine ID comes from the game client,
    // which runs on the player's hardware. It still refuses the payout.
    same_device_kills: 'same machine (client-reported)',
    shared_identity: 'shared payout',
    // Blocking since D-040 (2026-10-20).
    net_position: 'unexplained ledger',
    bonus_unwagered: 'unwagered bonus',
}

/**
 * Why this row cannot be approved, in the collapsed row.
 *
 * Distinct from the verdict beside it, and the distinction is the whole point of
 * the change. The verdict is advice -- "needs review", "don't pay yet" --
 * and a reviewer was always free to pay anyway. This is a refusal: the server
 * will not approve the row while it is showing, whatever this page does.
 *
 * So it renders whenever the server sends it, including when triage is switched
 * off entirely. Every other component here returns null in that case, which is
 * right for a suggestion and would be a hole in a gate.
 */
const WithdrawRequestBlock = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const block = withdrawRequest.payout_block
    if (!block?.blocked) return null

    return (
        <span
            title={blockWarning(block.reasons)}
            className='flex flex-row gap-1 items-center uppercase border rounded px-1 whitespace-nowrap font-bold text-red-400 border-red-500/60 bg-red-500/10'
        >
            <FontAwesomeIcon icon={faBan} />
            <span>{block.codes.map((code) => BLOCK_LABEL[code] ?? code).join(' + ')}</span>
        </span>
    )
}

/**
 * The verdict, in the collapsed row.
 *
 * A suggestion for whoever is reviewing and nothing more -- no control here
 * approves, declines or moves anything, and every one of these components
 * renders nothing at all when the backend sends no triage, which is what
 * happens whenever WITHDRAW_TRIAGE_ENABLED is off.
 */
const WithdrawRequestVerdict = () => {
    const { withdrawRequest, details } = useContext(WithdrawRequestContext)
    const triage = withdrawRequest.triage
    if (withdrawRequest.verdict_pending)
        return (
            <span
                title='Working out the verdict for this request'
                className='flex flex-row gap-1 items-center uppercase border rounded px-1 whitespace-nowrap text-white/40 border-white/10'
            >
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>checking…</span>
            </span>
        )
    if (withdrawRequest.checks_failed)
        return (
            <span
                title='The checks for this request failed to run. Approving still refuses anything they would block.'
                className='uppercase border rounded px-1 whitespace-nowrap text-amber-400 border-amber-400/40'
            >
                checks failed
            </span>
        )
    if (!triage) return null

    const style = VERDICT_STYLE[triage.verdict]
    // "One thing to check" does not say what. The check that raised it does,
    // so the pill carries its own reason and the row stops being a prompt to
    // go and find out what the prompt meant.
    // The reason is not on the pill.
    //
    // It was, and it made every row a different length: one account's reason
    // is eleven words and the next one's is two, so the date and the amount
    // to the right of them never lined up, and the queue could not be read
    // down a column. The reason is in the case, which is where somebody
    // reads it -- the pill's job on the row is to sort, not to explain.
    const failed = (triage.signals ?? []).filter((signal) => signal.counted)

    return (
        <span
            title={[style.title, ...failed.map((f) => f.text)].join(' — ')}
            className={twMerge(
                'flex max-w-full flex-row items-center gap-1 rounded border px-1.5 py-0.5',
                style.text,
                style.border
            )}
        >
            <FontAwesomeIcon icon={style.icon} />
            <span className='uppercase'>{style.label}</span>

        </span>
    )
}

/**
 * What this team already decided about this account, in the collapsed row.
 *
 * The single largest saving on the page is not a better check, it is not
 * re-running an investigation somebody already finished. A reviewer who can
 * see "paid 9 times, never declined, last approved by Sara on 14 Aug" spends
 * seconds on a row that used to cost minutes.
 *
 * Deliberately styled flat rather than green. It is context, and it suppresses
 * nothing -- a held row stays held however many times this account has been
 * paid before.
 */
const WithdrawRequestHistory = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const when = useStaffTime()
    const history = withdrawRequest.triage?.history
    if (!history) return null

    const seen = history.approved + history.declined
    // Others still in flight (requested, approved, processing) are not
    // decisions, but they are not "never withdrawn" either (PAY-27).
    const others = Math.max(0, (history.requests ?? 1) - 1)
    if (!seen && others > 0) {
        return (
            <span
                className='flex flex-row gap-1 items-center text-white/40 whitespace-nowrap'
                title='This account has other withdrawal requests, none of them paid or declined yet.'
            >
                (no decision yet on {others} other request{others === 1 ? '' : 's'})
            </span>
        )
    }
    if (!seen) {
        return (
            // The one fact on this row that changes how it should be read:
            // there is no history to lean on, so every judgement has to come
            // from the account itself.
            <span
                // Lavender, not amber: amber on this row means "a check wants
                // a look", and a first withdrawal is not a finding. Its own
                // colour so it reads as a different kind of fact.
                className='whitespace-nowrap rounded border border-fuchsia-300/60 bg-fuchsia-300/10 px-1.5 py-0.5 font-bold uppercase tracking-wide text-fuchsia-300'
                title='This account has never withdrawn before. Nothing has been paid or declined to judge it against.'
            >
                first withdrawal
            </span>
        )
    }

    const last =
        history.last_decided_at &&
        `${history.last_decision} ${when(history.last_decided_at)}${
            history.last_decided_by ? ` by ${history.last_decided_by}` : ''
        }`

    // "approved 17 Sep by Banjo" sat on the line of a request that is still
    // waiting for a decision, and read as the decision on THIS one. On a
    // payments screen that is the most expensive ambiguity there is, so the
    // word "previously" leads and the whole group is bracketed and dimmed
    // away from the live badges beside it.
    return (
        <span
            className='flex flex-row gap-1 items-center text-white/40 whitespace-nowrap'
            title={
                last
                    ? `Earlier withdrawals from this account. The last one was ${last}. ` +
                      'This request has not been decided.'
                    : 'Earlier withdrawals from this account'
            }
        >
            <FontAwesomeIcon icon={faClockRotateLeft} className='opacity-60' />
            <span className='uppercase tracking-widest text-[10px] text-white/30'>previously</span>
            <span>
                {history.approved} paid
                {history.declined > 0 && `, ${history.declined} declined`}
            </span>
            {last && <span className='text-white/30'>· last {last}</span>}
        </span>
    )
}

/**
 * Whether the anti-cheat caught this player, and where their integrity sits.
 *
 * Sits immediately after the verdict because it answers the question a
 * reviewer asks before any of the ledger arithmetic: is this somebody we
 * already know about. Both parts are shown together on purpose -- a score with
 * no context reads as an accusation, and "cheated" with no score gives a
 * reviewer nothing to weigh it against.
 *
 * The distinction the wording carries, and it is the important part:
 *
 * * **CONFIRMED CHEAT / CHEATED** is evidence. A human marked a cheat report
 *   actioned, or a detector from the trusted list fired. It is a statement
 *   about something that happened.
 * * **CHEAT REPORTS** is unfinished work: raised, not dismissed, nobody has
 *   concluded either way yet. It is a reason to look, and separated from the
 *   pill above precisely so it stops being read as one.
 * * **CLEARED** is a reviewer having read every report on the account and
 *   dismissed all of them. It suppresses the cheat pills rather than sitting
 *   beside them -- a dismissal is a person overruling the detector, and the
 *   screen must not go on asserting what that person just rejected.
 * * **the pill** is a ranking, out of 100, where 100 is clean. A low number
 *   means this account's measured play sits at the extreme end of the
 *   population. It is a reason to look, never a finding, and an account can
 *   sit low without having cheated at all.
 *
 * Nothing here blocks anything by itself. `held_by_gate` reports that the
 * integrity gate will refuse this approval, which is a separate and
 * configurable decision made in Django admin.
 */
const WithdrawRequestIntegrity = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const integrity = withdrawRequest.triage?.integrity
    if (!integrity) return null

    const score = integrity.score
    const tone =
        score === null
            ? 'text-white/40 border-white/20'
            : score >= 80
              ? 'text-green-500 border-green-500/40'
              : score >= 50
                ? 'text-amber-400 border-amber-400/40'
                : 'text-red-500 border-red-500/40'

    return (
        <span className='flex flex-row gap-1 items-center'>
            {/* Confirmed and merely reported are two pills, because they were
                one and it was the stronger of the two that got read. A single
                CHEATED pill fired on an actioned report AND on a bare detector
                hit, so a reviewer could not tell "a colleague concluded this
                person cheated" from "a machine flagged them", and both held up
                the same payout. */}
            {integrity.cheated && (
                <span
                    className='uppercase border rounded px-1 font-bold text-red-500 border-red-500/40'
                    title={
                        integrity.actioned_reports > 0
                            ? // D-045: automatic ACTIONED reports count here too.
                              'Cheating confirmed by a reviewer, or acted on automatically by the cheat policy'
                            : 'A trusted anti-cheat detector fired on this account'
                    }
                >
                    cheated
                </span>
            )}
            {/* Outstanding reports: raised, not dismissed, not yet concluded.
                Amber rather than red on purpose -- this is the queue's unfinished
                work, not a finding, and the whole point of the split is that an
                unread report must stop looking like a verdict. Disjoint from the
                pill above so the two counts add up rather than overlapping. */}
            {integrity.open_reports - integrity.actioned_reports > 0 && (
                <span
                    className='uppercase border rounded px-1 text-amber-400 border-amber-400/40'
                    title='Cheat reports nobody has looked at yet'
                >
                    {integrity.open_reports - integrity.actioned_reports} cheat report
                    {integrity.open_reports - integrity.actioned_reports === 1 ? '' : 's'}
                </span>
            )}
            {/* Somebody already did this work and found nothing. Shown because
                the alternative is a blank space that looks identical to an
                account nobody has examined, which is how the same player gets
                investigated twice. */}
            {integrity.cleared_by_review && (
                <span
                    className='uppercase border rounded px-1 text-green-500 border-green-500/40'
                    title='Every cheat report on this account was reviewed and dismissed'
                >
                    cleared
                </span>
            )}
            {/* Labelled, because unlabelled it read as a verdict. "100/100"
                in green beside a red DON'T PAY YET looks like the screen
                contradicting itself; "integrity 100" says plainly that it
                measures how this account plays, which is a different question
                from whether this money should move. */}
            <span
                className={twMerge('border rounded px-1 whitespace-nowrap', tone)}
                title={
                    score === null
                        ? 'No integrity profile yet: too few sessions to score'
                        : `${INTEGRITY_MEANING} It says nothing about whether this withdrawal should be paid. ` +
                          `${integrityBasisText(integrity)}.` +
                          (integrity.accuracy_scored
                              ? ''
                              : ' Aim accuracy was NOT measured (too few hits), so it cannot have lowered this score.')
                }
            >
                <span className='text-[10px] uppercase tracking-widest opacity-60'>integrity </span>
                {score === null ? 'not scored' : `${Math.round(score)}/100`}
                {/* The basis on the chip itself, not only in the tooltip: a 24
                    over 26 matches and a 24 over 6,000 are different claims and
                    the reviewer reads the chip, not the hover. */}
                {score !== null && integrity.sessions != null && (
                    <span className={isFewMatches(integrity) ? 'text-amber-400' : 'opacity-50'}>
                        {' · '}
                        {integrity.sessions.toLocaleString()} matches
                    </span>
                )}
                {/* Its age on the chip too (D-105): a profile counts until the
                    scoring version changes, so an old one still gates, and the
                    reviewer should see that it is old without hovering. */}
                {score !== null && integrity.computed_at && (
                    <span className='opacity-50'>
                        {' · '}scored {integrityAge(integrity.computed_at)}
                    </span>
                )}
            </span>
            {integrity.held_by_gate && (
                <span
                    className='uppercase border rounded px-1 text-red-500 border-red-500/40'
                    title='The integrity gate will refuse this approval until the account is cleared'
                >
                    held
                </span>
            )}
        </span>
    )
}

/** The one sentence, for the collapsed row.
 *
 * It exists so the queue can be read without opening anything, which is worth
 * a line. Opened, the case above states the same thing in more detail and in
 * better order, so the sentence becomes the same fact twice -- and on a row
 * that already carries a verdict, a score and a history, a third summary is
 * what makes the strip dense enough that none of it gets read.
 */
const WithdrawRequestTriageHeadline = () => {
    const { withdrawRequest, details } = useContext(WithdrawRequestContext)
    const triage = withdrawRequest.triage
    if (!triage || details) return null

    return (
        <div className='px-1 py-1 font-normal text-white/70 leading-relaxed'>{triage.headline}</div>
    )
}

/** Ring members, named, with the kills that ran to and from each.
 *
 *  The ring total sits in the label beside this and answers "does this account
 *  farm the group". It cannot answer the next question, which is which of them
 *  -- and a ring is very often one victim carrying nearly the whole count with
 *  the rest incidental. That was only readable by opening each name in turn.
 *
 *  Reads `victim_rows` and falls back to `usernames`: a reviewer holding this
 *  page open across a deploy would otherwise meet a crash instead of a row.
 */
const VictimNames = ({ ring }: { ring: any }) => {
    const rows: Array<any> =
        ring?.victim_rows ?? (ring?.usernames ?? []).map((username: string) => ({ username }))
    return (
        <Fragment>
            {rows.map((victim) => (
                <a
                    key={victim.username}
                    className='text-amber-400 underline'
                    href={`/team-tools/accounts/users/${victim.username}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={
                        victim.dealt === undefined
                            ? 'Opens in a new tab'
                            : `${victim.dealt} dealt to ${victim.username}, ` +
                              `${victim.taken} taken from them — opens in a new tab`
                    }
                >
                    {victim.username}
                    {victim.dealt !== undefined && ` (${victim.dealt}↑ ${victim.taken}↓)`}
                </a>
            ))}
        </Fragment>
    )
}

/** A full-width heading inside the fact grid.
 *
 *  The thirteen tiles ran as one undifferentiated block, so "deposited" sat
 *  four columns from "accounts on their machine" with nothing saying they
 *  answer different questions -- and a reviewer scanning for the money had to
 *  read the labels of everything else on the way past.
 *
 *  `col-span-full` rather than three separate grids: the tiles keep one set of
 *  column tracks, so they stay aligned across the groups instead of each
 *  section sizing its own columns.
 */
/** A section heading that folds its section away. Open by default; the
 *  content is `contents` so it still lays out in the parent grid. */
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => {
    const [open, setOpen] = useState(true)
    return (
        <>
            <button
                type='button'
                onClick={() => setOpen((v) => !v)}
                className='col-span-full flex flex-row items-center gap-2 border-b border-white/20 pb-1 pt-4 text-left text-xs font-bold uppercase tracking-widest text-white/80 first:pt-0 hover:text-white'
            >
                <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className='text-[10px] text-white/50' />
                {title}
            </button>
            {open && <div className='contents'>{children}</div>}
        </>
    )
}

/** One labelled half of the evidence block.
 *
 *  The address rows and the machine rows used to run together in one column, in
 *  whatever order the checks happened to be written -- address, address,
 *  machine, machine, machine, address -- so telling which claim a row was
 *  making meant reading it. They are different strengths of evidence: a VPN
 *  moves an address and does not move a machine. The accounts page already
 *  keeps them apart under two headings, and this is that same split, here.
 */
const EvidenceGroup = ({
    title,
    summary,
    defaultOpen = true,
    children,
}: {
    title: string
    summary?: React.ReactNode
    defaultOpen?: boolean
    children: React.ReactNode
}) => (
    // Folded unless it holds direct evidence against THIS player. On a heavy
    // player the victim rings alone listed 30-40 names (production, 2026-09-29:
    // dng1 41, NIGHTSEAWOLF 36, GGsBrian 31): somebody who has killed a thousand
    // different people has killed some who share an address, and that says
    // nothing about the killer. The summary line keeps the counts in view and
    // one click shows every name, so nothing a reviewer had is taken away.
    <details open={defaultOpen} className='group flex flex-col gap-1'>
        <summary className='cursor-pointer select-none list-none flex flex-row flex-wrap items-baseline gap-2 border-b border-white/10 pb-0.5'>
            <span className='text-[11px] uppercase tracking-widest text-white/60'>
                <span className='inline-block w-3 group-open:rotate-90 transition-transform'>›</span>
                {title}
            </span>
            {summary && <span className='text-white/40 normal-case'>{summary}</span>}
        </summary>
        <div className='flex flex-col gap-1 pt-1'>{children}</div>
    </details>
)

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** Addresses are information only (Ammar, 2026-09-29): with machine
 *  signatures reported, an address is a household or a carrier exit far more
 *  often than it is a person, and nothing on this page escalates on one any
 *  more. So the list always starts folded; the summary line keeps the counts. */
const addressIsDirect = (_facts): boolean => false

/** Any shared machine is flagged, so any shared one opens the list. */
const machineIsDirect = (facts): boolean =>
    facts.same_device_opponents?.length > 0 || facts.device_neighbour_names?.length > 0

const addressSummary = (facts): string => {
    const parts: string[] = []
    if (facts.same_ip_opponents?.length) parts.push(plural(facts.same_ip_opponents.length, 'account traded kills', 'accounts traded kills'))
    if (facts.ip_neighbour_names?.length) parts.push(`shares an IP with ${plural(facts.ip_neighbour_names.length, 'account', 'accounts')}`)
    if (facts.victim_ip_worst) parts.push(`${plural(facts.victim_ip_worst.victims ?? 0, 'victim', 'victims')} share an address with each other`)
    return parts.length ? parts.join(' · ') : 'nobody else on their addresses'
}

const machineSummary = (facts): string => {
    const parts: string[] = []
    if (facts.same_device_opponents?.length) parts.push(plural(facts.same_device_opponents.length, 'account traded kills', 'accounts traded kills'))
    if (facts.device_neighbour_names?.length) parts.push(`same machine as ${plural(facts.device_neighbour_names.length, 'account', 'accounts')}`)
    if (facts.victim_device_worst) parts.push(`${plural(facts.victim_device_worst.victims ?? 0, 'victim', 'victims')} share a machine with each other`)
    if (parts.length) return parts.join(' · ')
    return facts.devices_reported === 0 ? 'no machine reported' : 'nobody else on their machine'
}

/** One row inside a group: a grey label, then the names it introduces. */
const EvidenceRow = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
    <div className='flex flex-row flex-wrap gap-1 items-baseline'>
        <span className='text-white/60'>{label}</span>
        {children}
    </div>
)

const TriageFact = ({
    label,
    value,
    hint,
}: {
    label: string
    value: React.ReactNode
    /** What the label means, for the ones whose names are not self-evident.
     *  "Accounted for" and "unexplained" are arithmetic, not English, and a
     *  reviewer cannot act on a number whose definition they are guessing. */
    hint?: string
}) => (
    // A surface, not four hairlines. Thirteen transparent boxes on black read
    // as a wireframe; the faint fill is what makes them land as tiles and
    // lets the number inside carry the contrast.
    <div className='flex flex-col gap-0.5 rounded-md border border-white/15 bg-white/[0.05] px-2 py-1.5'>
        <span
            className={twMerge(
                'flex flex-row items-center gap-1 text-[11px] font-normal uppercase tracking-wide text-white/65',
                hint && 'cursor-help hover:text-white'
            )}
            title={hint}
        >
            {label}
            {/* A dotted underline is a convention people have to already know.
                An "i" is the thing they look for. */}
            {hint && <FontAwesomeIcon icon={faCircleInfo} className='opacity-50' />}
        </span>
        <span className='text-sm'>{value}</span>
    </div>
)

/** The numbers behind the sentence, so a reviewer can disagree with it. */
const WithdrawRequestTriageDetail = () => {
    const { withdrawRequest, verdictFailed } = useContext(WithdrawRequestContext)
    const triage = withdrawRequest.triage
    /* Declared above the early return below, because a hook cannot sit after
     * one. Which funding figure's rows are open, if any. */
    const [log, setLog] = useState<LedgerQuery | null>(null)
    // The buffer while this row's checks run. They take seconds for a heavy
    // player, and an empty panel in that time reads as "nothing to report".
    if (withdrawRequest.verdict_pending)
        return (
            <div className='flex flex-row gap-2 items-center p-3 text-white/50 font-normal'>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Checking this withdrawal…</span>
            </div>
        )
    if (verdictFailed)
        return (
            <div className='p-2 text-amber-400 font-normal'>
                Could not check this withdrawal. Collapse and reopen it to try again. Approving still
                refuses anything the checks would block.
            </div>
        )
    if (!triage) return null

    const facts = triage.facts
    // Every airdrop, USDC and promo credit: the funding chart's "airdropped"
    // and the total the breakdown under it adds up to. Display only -- the
    // checks still treat credit as not money in (`facts.funded`).
    const allAirdrops = Number(facts.airdropped ?? 0) + Number(facts.airdrop_received ?? 0)
    // Victims on one machine, less the accounts already listed as on THIS
    // account's machine -- those are in the table above.
    const onOwnMachine = new Set(
        (facts.device_neighbour_rows ?? []).map((row: { username: string }) => row.username)
    )
    // A table for a flag somebody dismissed is the evidence for a decision
    // already made; it goes with the flag.
    const dismissed = (key: string) =>
        (triage.signals ?? []).some((signal: any) => signal.key === key && signal.forgiven?.applies)
    const neighbourRows =
        dismissed('same_device_kills') || dismissed('shared_machine') ? [] : facts.device_neighbour_rows ?? []
    const otherVictimRows = dismissed('victim_device_cluster')
        ? []
        : (facts.victim_device_worst?.victim_rows ?? []).filter(
              (row: { username: string }) => !onOwnMachine.has(row.username)
          )
    /** Real money in from them, less real money already paid to them.
     *
     *  Deliberately not spawn fees less airdrops: a fee comes out of one
     *  player's balance and is paid to the players who win the match, so it
     *  is not ours to count. What crosses the boundary is a deposit one way
     *  and a withdrawal the other. */
    /* How much of this balance is BR1's money rather than another player's.
     *
     * THIS REPLACED `fees_paid - earned - airdropped`, WHICH WAS WRONG HERE.
     * That formula treats every dollar won as a dollar BR1 paid out. BR1 does
     * not fund match winnings: a kill pays the dead player's spawn fee. The
     * product says so in as many words -- "for every player you kill, earn
     * their spawn fee" (i18n/en/default.json) -- and it is measured, not
     * assumed: `bank/management/commands/pay_missing_kill_earnings.py` records
     * `earnings = fee * kills` holding at exactly 1.00 on 1,951 of 2,077 paid
     * production sessions. `withdraw_triage` says the same at `kills_earned`:
     * "winnings off other players: the spawn-fee share a kill pays".
     *
     * So spawn fees and kill winnings are players moving money between
     * themselves. BR1 collects the fees, pays out ~80% of them as winnings,
     * and keeps its service fee -- which means BR1 CANNOT NET-LOSE ON MATCH
     * PLAY, and a per-account figure saying otherwise is reading a pooled
     * quantity one row at a time.
     *
     * The old formula is still right in AGGREGATE -- across every player,
     * fees in less winnings out is exactly the retained service fee, which is
     * why `affiliate/selectors/revenue.py` pays affiliates on it and
     * AFFILIATE_PAGE.md says a losing player must net against the others
     * rather than be read alone. On ONE account it bills a winner for money
     * the players they killed funded. gromix showed as costing BR1 $37.87
     * while BR1's actual outlay to them was $15.03.
     *
     * What BR1 genuinely risks paying out is the credit it put there itself:
     * kill races (`kill_race.py` calls `system_topup()` to mint the prize
     * before paying it), loot, treasure hunts, race prizes, referral rewards
     * and airdrops. `earned` is every kind of winning and `kills_earned` is
     * the player-funded part of it, so the difference is the house-funded
     * part.
     *
     * The arithmetic is `withdraw_triage._house_funded`, not here: it is
     * tested there against the scenarios that actually differ -- a pure
     * depositor, an airdrop farmer, a kill-race farmer, a winning player, a
     * losing one, and a reversed prize -- and a number this screen uses to
     * decide whether to pay somebody should not be derived in a render.
     */
    const houseFunded = Number(facts.house_funded ?? 0)
    /** BR1's two sides, totalled on the server. See `_assemble`. */
    const houseIn = Number(facts.house_in ?? 0)
    const houseOut = Number(facts.house_out ?? 0)
    const houseNet = Number(facts.house_net ?? 0)

    /** What they put into the pot by playing, less what they took out of it.
     *
     *  A spawn fee is not the house's money -- it funds the prize the winners
     *  of that match take. So the honest question about somebody's PLAY is
     *  whether they put more into that pot than they took out: positive means
     *  their matches funded other players, negative means other players
     *  funded them. */
    const table = Number(facts.fees_paid ?? 0) - Number(facts.earned ?? 0)
    /** The spread check's own readings and the lines it draws them against. */
    const spreadData: any =
        (triage.signals ?? []).find((signal: any) => signal.key === 'opponent_spread')?.data ?? {}
    /* What an ordinary account would show over this many matches.
     *
     * The population rate times their match count, so the comparison is like
     * for like: an account with four hundred matches is not unusual for
     * having met more people than one with nine. */
    const matches = Number(facts.sessions ?? 0)
    const expectedKills = Math.round(Number(spreadData.kills_per_match ?? 0) * matches)
    const expectedOpponents = Math.round(Number(spreadData.opponents_per_match ?? 0) * matches)
    /* What has happened, not what would happen.
     *
     * This briefly showed where the position lands if the request is paid,
     * which is a forecast sitting where every other number on the screen is a
     * record -- and a figure that changes meaning depending on a decision
     * nobody has made yet is the kind of thing that gets quoted later as
     * though it were history. It goes back to zero until a payout actually
     * moves it. */
    /** Whether anything in "addresses and machines" has a number on it.
     *
     *  Every one of these is a risk signal, so nothing is the ordinary case
     *  and the ordinary case does not need seven tiles saying "none". */
    const sharesAnything =
        facts.ip_neighbours > 0 ||
        facts.device_neighbours > 0 ||
        facts.same_ip_accounts > 0 ||
        facts.same_device_accounts > 0 ||
        facts.victim_ip_clusters > 0 ||
        facts.victim_device_clusters > 0 ||
        facts.cheat_sessions > 0 ||
        facts.cheat_reports_actioned > 0
    /** Whether the aim reading disagrees with the population enough to be
     *  worth a second look. A quarter above the baseline, and only once there
     *  is a baseline to be above. */
    const aimWorthShowing =
        facts.headshot_baseline > 0 &&
        facts.headshot_rate > facts.headshot_baseline * 1.25
    /** Below the check's own floor a rate is shown grey and labelled, not
     *  amber. "40%" on 15 kills is 6 headshots, and amber made it read as a
     *  finding the check itself had declined to make. */
    const aimTooFew =
        Number(facts.headshot_kills_measured ?? 0) < Number(facts.headshot_min_kills ?? 20)
    return (
        <div className='flex flex-col gap-2 p-2'>
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-1'>
                {/* Two questions, two sections.
                 *
                 * "Money" ran the account's funding and its winnings through
                 * one grid, so "deposited 95.00" sat beside "won in game
                 * 187.61" as though they answered the same thing. They do not:
                 * one is what went in and out of the account, the other is
                 * what the game paid them, and a reviewer weighing a
                 * withdrawal is asking about one or the other, never both at
                 * once. */}
                <Section title='Where the balance came from'>
                {/* Two halves of one question, side by side. Stacked, the two
                    rings pushed the rest of the case off the screen for a
                    reader who had not scrolled. */}
                <div className='col-span-full grid gap-4 md:grid-cols-2 md:divide-x md:divide-white/15'>
                    <div className='flex flex-col gap-2 md:pr-4'>
                        <span className='text-sm font-bold uppercase tracking-widest text-white'>
                            Earned in game
                        </span>
                        <TotalWithParts
                    total={money(facts.earned)}
                    label='total earned'
                    hint='Everything the game has paid this account: kills, kill races and other winnings added together.'
                    parts={[
                        {
                            label: 'earned from kills',
                            value: facts.kills_earned,
                            display: money(facts.kills_earned),
                            tone: '#38bdf8',
                            hint: 'Their share of the spawn fees on players they killed. Money taken off other players.',
                            onOpen: () => setLog(LEDGER.kills),
                        },
                        {
                            label: 'earned in kill races',
                            value: facts.kill_race,
                            display: money(facts.kill_race),
                            tone: '#a78bfa',
                            hint: 'Kill-race prizes. Money BR1 put up, not money taken off other players.',
                            onOpen: () => setLog(LEDGER.killRaces),
                        },
                        ...(Number(facts.other_earned) > 0
                            ? [
                                  {
                                      label: 'other winnings',
                                      value: facts.other_earned,
                                      display: money(facts.other_earned),
                                      // Red in the bar: everything else in
                                      // "earned" is the game paying out for
                                      // play. This is not, so it is the
                                      // segment worth noticing.
                                      tone: '#ef4444',
                                      hint: 'Loot boxes, treasure hunts, races and referral rewards — money the game paid them for something other than playing a match.',
                                      onOpen: () => setLog(LEDGER.otherWinnings),
                                  },
                              ]
                            : []),
                    ]}
                />
                    </div>
                    <div className='flex flex-col gap-2 md:pl-4'>
                        <span className='text-sm font-bold uppercase tracking-widest text-white'>
                            Money put into the account
                        </span>
                        <TotalWithParts
                    total={money(Number(facts.deposited ?? 0) + allAirdrops)}
                    label='total funding'
                    hint='Everything that entered this account from outside the game: what the player paid in, plus every airdrop BR1 gave them (USDC and promo credit).'
                    parts={[
                        {
                            label: 'deposited',
                            value: facts.deposited,
                            display: money(facts.deposited),
                            tone: '#22c55e',
                            hint: 'Money the player paid in themselves.',
                            onOpen: () => setLog(LEDGER.deposits),
                        },
                        {
                            label: 'airdropped',
                            value: String(allAirdrops),
                            display: money(allAirdrops),
                            tone: '#fbbf24',
                            hint: 'Every airdrop BR1 gave them, USDC and promo credit. The breakdown is below.',
                            onOpen: () => setLog(LEDGER.airdrops),
                        },
                    ]}
                />
                {/* The airdrops above, by how they got them. Adds up to the
                    "airdropped" figure. */}
                {allAirdrops > 0 && (
                    <TotalWithParts
                        total={money(allAirdrops)}
                        label='airdrops by source'
                        hint='Every airdrop BR1 gave this player, by what it was for. Promo credit pays for spawns and cannot be withdrawn; USDC airdrops can.'
                        parts={[
                            ...(Number(facts.airdropped ?? 0) > 0
                                ? [
                                      {
                                          label: 'USDC airdrops',
                                          value: facts.airdropped,
                                          display: money(facts.airdropped),
                                          tone: '#22c55e',
                                          hint: 'Withdrawable USDC BR1 gave them: bonuses, compensation.',
                                          onOpen: () => setLog(LEDGER.usdcAirdrops),
                                      },
                                  ]
                                : []),
                            {
                                label: 'level-up rewards',
                                value: facts.airdrop_level_rewards,
                                display: money(facts.airdrop_level_rewards),
                                tone: '#a78bfa',
                                hint: 'Credit paid for reaching a new level.',
                                onOpen: () => setLog(LEDGER.levelUps),
                            },
                            {
                                label: 'deposit match',
                                value: facts.airdrop_deposit_match,
                                display: money(facts.airdrop_deposit_match),
                                tone: '#38bdf8',
                                hint: 'The bonus credit matched to a deposit.',
                                onOpen: () => setLog(LEDGER.depositMatch),
                            },
                            {
                                label: 'manual airdrops',
                                value: facts.airdrop_manual,
                                display: money(facts.airdrop_manual),
                                tone: '#f472b6',
                                hint: 'Credit a staff member gave by hand: compensation, tester bonuses, giveaways. Click to see who and why.',
                                onOpen: () => setLog(LEDGER.manualAirdrops),
                            },
                            {
                                label: 'other airdrops',
                                value: facts.airdrop_other,
                                display: money(facts.airdrop_other),
                                tone: '#fbbf24',
                                hint: 'Every other automatic grant: promotions and system rewards that are not level-ups or deposit matches.',
                                onOpen: () => setLog(LEDGER.otherAirdrops),
                            },
                        ].filter((part) => Number(part.value) > 0)}
                    />
                )}
                    </div>
                </div>
                {log && (
                    <FundingLog
                        userId={withdrawRequest.user_id}
                        query={log}
                        onClose={() => setLog(null)}
                    />
                )}
                {Number(facts.unexplained) > 0 && (
                    <TriageFact
                        hint='Money that arrived in this balance by a route this check cannot name — not a deposit, an airdrop, winnings, a refund or a swap. It should be zero. Anything else is a manual adjustment or a ledger bug, and is worth holding the payout over.'
                        label='unexplained'
                        value={<span className='text-red-500'>{money(facts.unexplained)}</span>}
                    />
                )}

                </Section>

                <Section title='Account data'>

                {/* Three counts and two positions, and they are not the same
                    kind of thing. The counts describe how much account there
                    is; the positions say who is up on it. Side by side in one
                    row of identical tiles, the two read as five readings of
                    one number. */}
                <div className='col-span-full grid items-stretch gap-4 md:grid-cols-[minmax(0,1fr),minmax(0,1.2fr)]'>
                    <div className='flex flex-col gap-1'>
                        <span className='text-[10px] uppercase tracking-widest text-white/45'>
                            How much they have played
                        </span>
                        <div className='flex h-full flex-col gap-3 rounded-md border border-white/15 bg-white/[0.05] px-3 py-3'>
                            {/* Matches alone at the top, because every bar
                                under it is measured over that number -- and
                                kills was printed twice, once here and once as
                                its own gauge. */}
                            <div className='flex flex-row items-baseline gap-2'>
                                <span className='text-3xl font-bold tracking-tight text-white'>
                                    {facts.sessions}
                                </span>
                                <span className='text-[11px] uppercase tracking-widest text-white/50'>
                                    matches played
                                </span>
                            </div>
                            {/* The two readings the checks actually judge, each
                                against the line it is judged by. A bare "9
                                opponents" means nothing until you know the
                                check wants ten, and nobody holds that number
                                in their head. */}
                            {/* Against a person, not against a threshold.
                                
                                "Thin below 10" is a rule somebody wrote down;
                                "an ordinary player would have faced 22 in
                                this many matches" is a comparison a reviewer
                                can weigh. The rates are medians over real
                                accounts, scaled to the matches this one has
                                played, so the two numbers are like for
                                like. */}
                            {/* Shown whether or not the comparison is
                                available. These two vanished entirely when
                                `population_rates` came back zero -- which it
                                does if nothing qualifies in its 30-day window
                                -- taking two of the three readings off the
                                screen and leaving "53 matches played" with no
                                kills and no opponents under it. How many
                                people they killed is a fact about the account;
                                only the "an ordinary player" mark depends on
                                the population, and `Gauge` now omits just the
                                mark. */}
                            <Gauge
                                    label='different people they killed'
                                    value={Number(facts.opponents)}
                                    mark={expectedOpponents}
                                    max={Math.max(Number(facts.opponents), expectedOpponents, 1) * 1.3}
                                    markLabel='an ordinary player'
                                    bad={expectedOpponents > 0 && Number(facts.opponents) < expectedOpponents * 0.6}
                                    display={String(facts.opponents)}
                                    hint={
                                        'Distinct people this account has KILLED — not people who killed them. ' +
                                        (expectedOpponents > 0
                                            ? `Over ${facts.sessions} matches an ordinary player kills about ${expectedOpponents} different people. `
                                            : '') +
                                        'Many kills against few people is the shape of farming a small set of accounts.'
                                    }
                                />
                            <Gauge
                                    label='kills'
                                    value={Number(facts.kills)}
                                    mark={expectedKills}
                                    max={Math.max(Number(facts.kills), expectedKills, 1) * 1.3}
                                    markLabel='an ordinary player'
                                    bad={expectedKills > 0 && Number(facts.kills) > expectedKills * 2}
                                    display={String(facts.kills)}
                                    hint={
                                        expectedKills > 0
                                            ? `Over ${facts.sessions} matches an ordinary player takes about ${expectedKills}.`
                                            : 'How many kills this account has made. The comparison with an ordinary player is unavailable right now.'
                                    }
                                />
                            {/* "49% on their top 3" is a ratio whose subject
                                the reader has to reconstruct. The sentence
                                says it: half their kills were three people. */}
                            {/* Counts, not just a share, and no bar at all
                                below the floor the check itself uses. With 15
                                kills the top three cannot be under 3/15 = 20%
                                however somebody plays, so "27%" on a confident
                                bar was arithmetic dressed as a reading. */}
                            {Number(spreadData.kills ?? 0) <
                            Number(spreadData.min_kills_for_spread ?? 20) ? (
                                <span
                                    className='text-[11px] text-white/45'
                                    title='Who their kills land on is only judged once there are enough kills for it to mean something.'
                                >
                                    Too few kills to judge who they farm ({Number(spreadData.kills ?? 0)} of{' '}
                                    {Number(spreadData.min_kills_for_spread ?? 20)} needed)
                                </span>
                            ) : (
                                <Gauge
                                    label='of their kills were on the same 3 players'
                                    value={Number(spreadData.top3_share ?? 0) * 100}
                                    mark={Number(spreadData.concentration_limit ?? 0.6) * 100}
                                    max={100}
                                    markLabel=''
                                    bad={
                                        Number(spreadData.top3_share ?? 0) >=
                                        Number(spreadData.concentration_limit ?? 0.6)
                                    }
                                    display={`${(spreadData.top3 ?? []).reduce(
                                        (sum: number, row: any) => sum + Number(row.kills ?? 0),
                                        0
                                    )} of ${Number(spreadData.kills ?? 0)} (${Math.round(
                                        Number(spreadData.top3_share ?? 0) * 100
                                    )}%)`}
                                    hint={`All-time. Of everything this account killed, this many kills were on the same three players. Somebody working through the lobby spreads their kills across it; somebody farming a set of accounts does not. It is flagged at ${Math.round(
                                        Number(spreadData.concentration_limit ?? 0.6) * 100
                                    )}%.`}
                                />
                            )}
                            {/* Named, with the kills each. A share is a
                                finding nobody can act on until they know
                                which accounts it is about. */}
                            {(spreadData.top3 ?? []).length > 0 && (
                                <div className='flex flex-row flex-wrap items-center gap-x-3 gap-y-1 text-[11px]'>
                                    {(spreadData.top3 ?? []).map((row: any) => (
                                        <span key={row.username} className='flex flex-row items-center gap-1'>
                                            <span className='text-white/70'>{row.username}</span>
                                            <span className='text-white/35'>{row.kills}</span>
                                            <a
                                                href={`/team-tools/accounts/users/${row.username}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                onClick={(e) => e.stopPropagation()}
                                                title={`Open ${row.username} in a new tab`}
                                                className='rounded border border-white/15 px-1 text-[10px] text-white/40 hover:border-white/40 hover:text-white'
                                            >
                                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                            </a>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='flex h-full flex-col gap-1'>
                        <span className='text-[10px] uppercase tracking-widest text-white/45'>
                            Who is up on this account
                        </span>
                        <div className='flex h-full flex-col justify-around gap-3 rounded-md border border-white/15 bg-white/[0.05] px-3 py-3'>
                            <Ledger
                                title={
                                    table === 0
                                        ? 'They have not won or lost'
                                        : table < 0
                                          ? 'They are making money'
                                          : 'They are losing money'
                                }
                                net={-table}
                                inLabel='they won'
                                inValue={Number(facts.earned ?? 0)}
                                outLabel='fees they paid'
                                outValue={Number(facts.fees_paid ?? 0)}
                                hint={`They have paid ${money(facts.fees_paid)} in spawn fees and won ${money(facts.earned)} back.`}
                            />
                            <Ledger
                                title={
                                    houseNet === 0
                                        ? 'We have not made or lost money'
                                        : houseNet < 0
                                          ? 'We are losing money'
                                          : 'We are making money'
                                }
                                net={houseNet}
                                /* "What we KEPT", not "what we took".
                                 *
                                 * It read "what we took  $0.00" on an account
                                 * that had plainly paid spawn fees, which
                                 * looks like the screen is broken. We do take
                                 * the fees -- we just hand nearly all of them
                                 * back out as winnings, and what stays is the
                                 * service fee. "Kept" is the word for that. */
                                inLabel='they paid us'
                                inValue={houseIn}
                                inHint={
                                    `Spawn fees paid in real USDC ${money(facts.fees_paid)}` +
                                    `, store ${money(facts.store_spend)}` +
                                    `, packs ${money(facts.pack_spend)}` +
                                    `, marketplace fees ${money(facts.marketplace_fees)}` +
                                    `, withdrawal fees ${money(facts.withdraw_fees)}` +
                                    `, withheld ${money(facts.withheld)}.` +
                                    ` Our ${money(facts.service_fee_kept)} service fee is not a separate line` +
                                    ` — it is already here, as a smaller payout on the other side.`
                                }
                                outLabel='we paid them'
                                outValue={houseOut}
                                outHint={
                                    `Everything we have credited this account: match winnings ${money(facts.kills_earned)}` +
                                    `, kill races ${money(facts.kill_race)}` +
                                    `, other prizes ${money(facts.other_earned)}` +
                                    `, airdrops ${money(facts.airdropped)}.` +
                                    ` ${money(facts.house_funded)} of it is money BR1 funded itself` +
                                    ` rather than another player's spawn fee.`
                                }
                                hint={
                                    'Real money in, real money out. Every spawn fee lands with us and every payout ' +
                                    'leaves us, so this is simply what they handed over less what we handed back. ' +
                                    'Deposits are not counted — that is their own money, held until they spend ' +
                                    'it or take it back. Fees paid from an airdrop balance are not counted either, ' +
                                    'because that was our own promo credit rather than money arriving.'
                                }
                            />
                            {Number(facts.promo_fees ?? 0) > 0 && (
                                /* Promo credit, named and deliberately not
                                 * counted.
                                 *
                                 * A spawn fee is split across two currencies
                                 * -- `JoinFeeSplit.airdrop_percent`, 30% by
                                 * default -- so a share of every fee is paid
                                 * with credit BR1 handed out. Taking that back
                                 * is not profit: it is our own voucher
                                 * returning, and it is already left out of
                                 * "they paid us" above.
                                 *
                                 * Shown anyway, because invisible is not the
                                 * same as understood. Without this line the
                                 * fees figure quietly disagrees with the one
                                 * on the player's own side of the screen, and
                                 * nothing says why. */
                                <span
                                    className='cursor-help text-white/45 hover:text-white'
                                    title='Spawn fees are paid partly from an airdrop balance and partly in USDC, but winnings are paid entirely in USDC. Promo credit coming back is not money arriving, so it is not counted as ours. It becomes a real cost when somebody wins it.'
                                >
                                    Plus {money(facts.promo_fees)} of their fees paid in promo credit
                                    &mdash; not counted, it was ours
                                </span>
                            )}
                            <NftsHeld facts={facts} />
                        </div>
                    </div>
                </div>


                </Section>

                {(neighbourRows.length > 0 || otherVictimRows.length > 0) && (
                    <>
                        <Section title='Shared machine'>
                        {/* Amber, not red. The refusal is stated once, at the
                            top, under what got this flagged; down here it is
                            the evidence behind it, and a second red panel
                            competes with the one that is actually the
                            decision. */}
                        <div className='col-span-full flex flex-col gap-2 rounded-md border border-amber-400/40 bg-amber-400/[0.06] px-3 py-2'>
                            {neighbourRows.length > 0 && (
                                <>
                                    <span className='font-normal text-amber-300'>
                                        Multiple accounts on this machine. The machine ID is
                                        client-reported: the game client sends it.
                                    </span>
                                    <MachineAccounts
                                        rows={neighbourRows}
                                        who={withdrawRequest.created_by}
                                        fresh={arrivedOn(triage.signals, ['same_device_kills', 'shared_machine', 'shared_device']).names}
                                    />
                                </>
                            )}
                            {/* Other side of the same question: players this
                                account kills who sit on one machine with each
                                other -- one person feeding from several
                                accounts. */}
                            {otherVictimRows.length > 0 && (
                                <div className='flex flex-col gap-1 pt-1'>
                                    <span className='font-normal text-amber-300'>
                                        Players {withdrawRequest.created_by} killed who share a machine with each other.
                                    </span>
                                    <PairFold
                                        fresh={arrivedOn(triage.signals, ['victim_device_cluster']).names}
                                        rows={otherVictimRows}
                                        hwid={facts.victim_device_worst.hwid_short}
                                        who={withdrawRequest.created_by}
                                    />
                                </div>
                            )}
                        </div>
                        </Section>
                    </>
                )}

                {/* The readings that say somebody may be working the game
                    rather than playing it.
                    
                    Said as sentences, not as counts. "1 group" is a number a
                    reviewer has to turn back into a fact before it means
                    anything, and the fact is the short version: their victims
                    share a machine.

                    Victims sharing an ADDRESS is not here. A household plays
                    from one address and beating your flatmate is not
                    suspicious; it was raising a flag on the ordinary case. A
                    machine is different -- one machine is usually one person,
                    so a set of victims on one machine is a set of accounts
                    somebody may be feeding kills to. That one already
                    escalates the verdict to "needs review". */}

            </div>

            {/* The address and machine evidence rows are gone.
             *
             * They were two collapsibles that summarised themselves with
             * "3 victims share an address with each other" and "nobody else
             * on their machine" -- a finding about addresses stated on every
             * request, including the ones where the answer is nothing.
             *
             * Addresses are no longer reported at all: a household plays from
             * one address, so sharing one is the ordinary case and flagging it
             * trained people to ignore the section. What survives is the
             * machine, under Suspicious activity, where it belongs. */}
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-1'>
                {/* Only when it is out of line.
                 *
                 * Headshot rate is already IN the integrity score, and heavily
                 * -- the aim analyser's reading carries the largest weight of
                 * any metric in the set, with headshot kills counted
                 * separately beside it. Printing it again as its own section
                 * on every request is the same fact twice, and "0% vs 0%" on
                 * an account with no telemetry is neither.
                 *
                 * It earns a tile when it disagrees with the population,
                 * because that is the one case where a reviewer wants the
                 * number behind the score rather than the score. */}
                {aimWorthShowing && (
                    <>
                        <Section title='Aim'>
                        <TriageFact
                            label='headshots'
                            value={
                                <span
                                    className={aimTooFew ? 'text-white/60' : 'text-amber-400'}
                                    title='Their last 30 days of play against everybody’s last 30 days, self-kills left out of both.'
                                >
                                    {facts.headshot_kills ?? '?'} of {facts.headshot_kills_measured ?? '?'} kills (
                                    {Math.round(facts.headshot_rate * 100)}%)
                                    <span className='ml-1 text-white/40'>
                                        vs {Math.round(facts.headshot_baseline * 100)}% average
                                    </span>
                                    {aimTooFew && (
                                        <span className='ml-1 text-white/40'>
                                            · too few kills to judge
                                        </span>
                                    )}
                                </span>
                            }
                        />
                        </Section>
                    </>
                )}
            </div>
            {/* The findings are stated once, in the case at the top. They
                were repeated here under the tiles, so a reviewer read
                "account is 0 days old with 0 sessions played" twice on the
                way down a single screen. */}
        </div>
    )
}

/**
 * The case, in four answers, above everything else in the expanded row.
 *
 * Opening a request used to land on MONEY / PLAY / ADDRESSES: thirteen tiles,
 * a machine list, an aim figure and then six hundred sessions, all in the same
 * weight. Everything a decision needs was on the page and none of it led, so
 * the reviewer assembled the case themselves, every time, from the same parts
 * in the same order.
 *
 * So the page states it instead. How much, what tripped it, what argues the
 * other way, and what nobody has settled yet -- each from the material that
 * was already there, none of it new. The tiles keep their place underneath for
 * when one of these four answers needs checking.
 *
 * "Still open" is its own column on purpose. An unread cheat report is not
 * evidence for or against; it is work nobody has done, and lumping it in with
 * findings is how it gets read as one.
 */
const WithdrawRequestCase = () => {
    const { withdrawRequest } = useContext(WithdrawRequestContext)
    const triage = withdrawRequest.triage
    const block = withdrawRequest.payout_block
    const integrity = triage?.integrity
    const history = triage?.history
    const facts = triage?.facts
    /** What the account held when they asked for this.
     *
     *  The live USDC balance with this request's own debit added back. The
     *  balance already excludes it -- `WITHDRAW_REQUEST` is written the moment
     *  the request is made -- so without adding it back the share below would
     *  be measured against what is left AFTER the thing being measured.
     *
     *  Previously reconstructed as `funded + earned - withdrawn`, which
     *  ignores every spawn fee they have paid and everything they have bought,
     *  so it overstated the balance on any account that had played. This is
     *  the sum of their USDC ledger, which is what the balance is. */
    const available = facts
        ? Number(facts.balance ?? 0) + Number(facts.this_request ?? 0)
        : null
    /** Real money in, less real money out.
     *
     *  NOT spawn fees less airdrops, which is what this was and was wrong:
     *  a spawn fee is not revenue. It is taken from the player's balance and
     *  paid straight back out to the players who win the match, so counting
     *  it as ours double-counts money that ends up in somebody else's
     *  balance and is withdrawn from there.
     *
     *  What actually leaves the business is a withdrawal, and what actually
     *  arrives is a deposit. Airdrops are not a cost on their own either --
     *  we only lose on one when somebody plays it up and takes it out, and
     *  the withdrawal already counts that. So: deposited, less paid out. */
    const house = facts ? Number(facts.deposited ?? 0) - Number(facts.withdrawn ?? 0) : null

    /* A check can fail to pass without being a reason to stop.
     *
     * "23% headshots, above the 23% average but not far enough to be a
     * finding" is a check saying explicitly that it found nothing -- it has
     * no `escalates_to`, and it did not move the verdict. Filed under "why it
     * stopped here" beside a real finding, it made LOOKS ROUTINE look like a
     * mistake and gave a reviewer two things to weigh where there was one.
     *
     * So the two are split on the thing that distinguishes them: whether the
     * check escalated the verdict. */
    /* One finding per thing that is wrong, not one per check that noticed it.
     *
     * Four separate checks look at the machine -- does another account sign in
     * from it, has another WITHDRAWING account reported it, were kills traded
     * on it, do this account's victims share it -- and on a real ring all four
     * fire. The reviewer got five bullets, including the payout block quoting
     * one of them back, all naming the same two accounts.
     *
     * So the machine family collapses to its strongest line, and anything the
     * payout block already said is not said again underneath it.
     */
    const MACHINE_KEYS = ['shared_machine', 'shared_device', 'same_device_kills', 'device_twin', 'victim_device_cluster']
    const counted = (triage?.signals ?? []).filter((signal: any) => signal.counted)
    const machine = counted.filter((signal: any) => MACHINE_KEYS.includes(signal.key))
    /* Confirmed cheating is not listed. Whoever confirms a report bans the
     * player in the same decision (the owner's workflow), so the line only
     * repeats what the ban already says. The payout gate still holds on it,
     * and the warning under Approve still says why. */
    const isConfirmed = (text: string) => /^Cheating confirmed/.test(text)
    const rest = counted.filter(
        (signal: any) => !MACHINE_KEYS.includes(signal.key) && !isConfirmed(signal.text)
    )

    const blocked = block?.blocked
        ? block.reasons
              // Each reason carries the signal behind it (`codes` runs beside
              // `reasons`), so it gets the log and "dismiss".
              .map((r: string, i: number) => ({
                  text: r,
                  hard: true,
                  level: 'hold',
                  signal: counted.find((signal: any) => signal.key === block.codes?.[i]),
              }))
              .filter((item: any) => !isConfirmed(item.text))
        : []
    const said = new Set(blocked.map((item) => item.text))
    // A machine block already names the machine; its weaker siblings would
    // only say it again in yellow.
    const machineBlocked = blocked.some((item: any) => MACHINE_KEYS.includes(item.signal?.key))

    const against = [
        ...blocked,
        // The longest machine line is the one carrying the names and the
        // kills; the others are subsets of it.
        ...(machine.length && !machineBlocked
            ? [machine.reduce((a: any, b: any) => (b.text.length > a.text.length ? b : a))]
            : []
        )
            .filter((signal: any) => !said.has(signal.text))
            .map((signal: any) => ({
                text: signal.text,
                hard: false,
                level: signal.escalates_to ?? 'review',
                signal,
            })),
        ...rest
            .filter((signal: any) => !said.has(signal.text))
            .map((signal: any) => ({
                text: signal.text,
                hard: false,
                level: signal.escalates_to ?? 'review',
                signal,
            })),
    ]

    /* Checks a reviewer already looked at and let go.
     *
     * Shown, not hidden. A forgiven flag is still a true thing about the
     * account -- the point is that somebody decided it was explained, not
     * that it stopped being so -- and a page that quietly drops it would be
     * lying to the next reviewer, who would then have no idea why a known
     * awkward account sails through.
     *
     * One that has LAPSED appears back in the column above, because by then
     * it is a different finding: bigger than the one that was cleared, or
     * past the date somebody asked to look again. */
    const pardoned = (triage?.signals ?? []).filter(
        (signal: any) => signal.forgiven?.applies
    )

    // Checks that did not escalate are dropped entirely rather than given a
    // column of their own. "23% headshots, above the 23% average but not far
    // enough to be a finding" is a measurement of how somebody aims: it is an
    // input to the integrity score, which is already on the row, and printing
    // it as prose invites a reviewer to weigh a thing the check has just said
    // does not weigh.

    const forPaying: string[] = []
    if (history && history.approved > 0 && history.declined === 0)
        forPaying.push(
            `Paid ${history.approved} time${history.approved === 1 ? '' : 's'} before, never declined.`
        )
    // Dismissed reports are not mentioned here any more. This screen only
    // speaks about what is still standing; the account page keeps the history.
    //
    // "Plays unremarkably" only when aim accuracy was actually measured. The
    // score starts at 100 and loses points only for what was measured, so an
    // account with too few hits for the aim checks read 100 and was called
    // unremarkable beside "40% headshots" -- the CEO's first screenshot.
    if (integrity && integrity.score !== null && integrity.score >= 80)
        forPaying.push(
            integrity.accuracy_scored
                ? `Plays unremarkably — integrity ${Math.round(integrity.score)} out of 100.`
                : `Nothing unusual found — integrity ${Math.round(integrity.score)} out of 100, but too few hits to measure their aim.`
        )
    if (triage && (triage.signals ?? []).every((signal: any) => signal.ok))
        forPaying.push('Every check passed.')

    /* "Still open" is work nobody has done, so each line goes WHERE THE WORK
     * IS. An unread cheat report named on a payout screen with no way to
     * reach it leaves the reviewer to go and find the account by hand, which
     * is how it stays unread. */
    const open: { text: string; href?: string }[] = []
    const unread = (integrity?.open_reports ?? 0) - (integrity?.actioned_reports ?? 0)
    // What they are about, from the signals the open reports tripped.
    const topics: string[] =
        ((triage?.signals ?? []).find((signal: any) => signal.key === 'cheat_history')?.data?.open_topics ?? []) as string[]
    if (unread > 0)
        open.push({
            text:
                `${unread} cheat report${unread === 1 ? '' : 's'} raised` +
                (topics.length ? ` (${topics.join(', ')})` : '') +
                ', not yet concluded either way.',
            // Straight to the panel that holds them, not the account's front
            // page -- a link that lands you one more click away is a link that
            // did half its job.
            href: `/team-tools/accounts/users/${encodeURIComponent(
                withdrawRequest.created_by
            )}?tab=integrity`,
        })
    if (withdrawRequest.verdict_pending) open.push({ text: 'The checks are still running.' })
    // "Not enough sessions to score" is not outstanding work -- nobody is
    // going to go and do it, and the row already says INTEGRITY NOT SCORED.
    // A second sentence about it only makes the column look busier than the
    // case is.

    /* Only the columns that have something in them.
     *
     * A heading over "Nothing on the account speaks for it either way" is a
     * line of reading that ends where it started. An empty column is not a
     * reassurance worth three words of layout, so it does not render, and
     * when all three are empty the block collapses to one sentence. */
    // Red and a warning triangle said "refused" on a row whose verdict is
    // "needs review". The heading takes the verdict's own colour and
    // icon, so the case and the pill on the row agree with each other.
    //
    // "Why it stopped here" also overstated it: nothing stopped, the checks
    // raised a flag and the row is still payable.
    const verdictStyle = triage ? VERDICT_STYLE[triage.verdict] : null

    const columns = [
        {
            title: 'Why this withdrawal got flagged',
            icon: block?.blocked ? faWarning : (verdictStyle?.icon ?? faWarning),
            tone: block?.blocked ? 'text-red-400' : (verdictStyle?.text ?? 'text-red-400'),
            items: against,
        },
        {
            title: 'What argues for paying',
            icon: faCheckCircle,
            tone: 'text-green-500',
            items: forPaying.map((text) => ({ text, hard: false })),
        },
        {
            title: 'Still open',
            icon: faStopwatch,
            tone: 'text-amber-400',
            items: open.map((item) => ({ ...item, hard: false })),
        },
    ].filter((column) => column.items.length > 0)

    if (!triage && !block?.blocked) return null

    return (
        <div className='m-2 flex flex-col gap-3 rounded-lg border border-white/15 bg-white/[0.04] p-4 shadow-lg shadow-black/40'>
            <div className='flex flex-row flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-white/10 pb-2'>
                {/* Whose request, then what it is. The name was on the row
                    behind the modal, which is blurred out once it opens, and
                    the two read as one long sentence when they sat on a
                    line together. */}
                <span className='flex min-w-0 flex-col gap-0.5'>
                    <span className='truncate text-xl font-bold text-white'>
                        {withdrawRequest.created_by}
                    </span>
                    <span className='flex flex-row flex-wrap items-baseline gap-2'>
                        <span className='text-[11px] uppercase tracking-widest text-white/50'>
                            Withdraw request
                        </span>
                        <span className='text-2xl font-bold tracking-tight text-white'>
                            {money(withdrawRequest.amount)} {currencyLabel(withdrawRequest.currency)}
                        </span>
                        {/* What the amount actually MEANS, in two parts.
                          *
                          * THE FEE. `amount` is what the player receives;
                          * `token_amount` is what comes off their balance.
                          * They differ by the withdrawal fee, which BR1 keeps
                          * and which has no ledger row of its own -- so the
                          * only place anyone can see it is here, by
                          * differencing the two. Approving "$36.20" while the
                          * account is debited $36.75 is a gap worth naming on
                          * the screen where the approving happens.
                          *
                          * THE SHARE, said in words. "72% of their balance"
                          * is a number a reviewer then has to judge. Whether
                          * somebody is taking a slice or emptying the account
                          * is the actual question, and the sentence answers
                          * it. */}
                        {available !== null && (
                            <span className='font-normal text-white/60'>
                                &mdash; {shareOfBalance(Number(withdrawRequest.amount), available)}
                            </span>
                        )}
                        {withdrawRequest.token_amount != null &&
                            Number(withdrawRequest.token_amount) >
                                Number(withdrawRequest.amount) && (
                                <span
                                    className='cursor-help font-normal text-white/45 hover:text-white'
                                    title='The player is debited the larger figure and paid the smaller one. The difference is the withdrawal fee, which BR1 keeps and which is not written to the ledger anywhere.'
                                >
                                    {money(withdrawRequest.token_amount)} comes off their balance;
                                    we keep{' '}
                                    {money(
                                        (
                                            Number(withdrawRequest.token_amount) -
                                            Number(withdrawRequest.amount)
                                        ).toFixed(2)
                                    )}{' '}
                                    as the fee
                                </span>
                            )}
                    </span>
                </span>

                {/* Moved off the row, where they were three things to read
                    before learning whose request it was. */}
                <span className='flex flex-row flex-wrap items-center gap-1'>
                    <WithdrawRequest.Verdict />
                    <WithdrawRequest.Integrity />
                    <WithdrawRequest.History />
                </span>

                {facts?.account_age_days !== undefined && (
                    <span className='ml-auto flex flex-col items-end'>
                        <span className='text-[10px] uppercase tracking-widest text-white/40'>Account age</span>
                        <span className='text-base font-bold text-white'>
                            {ageInWords(facts.account_age_days)}
                        </span>
                    </span>
                )}

                {/* The status was a pill here saying "waiting for a decision"
                    -- which is what every request in this queue is, and why
                    it is open in front of somebody. It survives on a row that
                    has already been decided, where it says something. */}
                {withdrawRequest.status !== 'requested' && (
                    <span className='ml-auto rounded-full border border-white/15 px-3 py-0.5 font-normal text-white/50'>
                        Already {withdrawRequest.status}
                    </span>
                )}
            </div>

            {columns.length === 0 ? (
                // Not a row of empty headings. Three columns saying "nothing"
                // is three things to read that say less than one line does.
                <div className='flex flex-row items-center gap-2 font-normal text-white/50'>
                    <FontAwesomeIcon icon={faCheckCircle} className='text-green-500/70' />
                    <span>Nothing flagged this. Every check passed.</span>
                </div>
            ) : (
                <div
                    className={twMerge(
                        'grid gap-x-6 gap-y-3',
                        columns.length === 1 ? '' : columns.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
                    )}
                >
                    {columns.map((column) => (
                        <CaseColumn key={column.title} title={column.title} icon={column.icon} tone={column.tone}>
                            {column.items.map((item: any, i) => (
                                <li
                                    key={i}
                                    title={item.text}
                                    className={
                                        // Anything about a shared machine ID is red too.
                                        item.hard || item.level === 'hold' || MACHINE_SIGNALS.includes(item.signal?.key)
                                            ? 'text-red-400'
                                            : 'text-amber-300'
                                    }
                                >
                                    {/* No "Refused." prefix. The row's verdict
                                        already says the payout is refused, and
                                        the red on this line says which finding
                                        did it -- the word was a third copy of
                                        the same fact. */}
                                    {item.href ? (
                                        <a
                                            href={item.href}
                                            target='_blank'
                                            rel='noreferrer'
                                            // Opened here rather than left to the link: the
                                            // page's own navigation was keeping it in this tab.
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                window.open(item.href, '_blank', 'noopener')
                                            }}
                                            className='underline decoration-dotted underline-offset-2 hover:text-white'
                                        >
                                            {headlineOf(item.text)}
                                        </a>
                                    ) : (
                                        newcomerLine(item.signal) ?? headlineOf(item.text)
                                    )}
                                    {/* A block carries the check that raised
                                        it; forgiving that check lifts it. */}
                                    {/* Same people, dismissed on another
                                        account: said, not applied. */}
                                    {(item.signal?.dismissed_elsewhere ?? []).map((d: any) => (
                                        <span
                                            key={d.username}
                                            className='block pt-0.5 text-[11px] font-normal text-white/45'
                                        >
                                            Dismissed for {d.username}
                                            {d.by ? ` by ${d.by}` : ''}: “{d.reason}”
                                        </span>
                                    ))}
                                    {item.signal && (
                                        <ForgiveFlag
                                            userId={withdrawRequest.user_id}
                                            signal={item.signal}
                                        />
                                    )}
                                </li>
                            ))}
                        </CaseColumn>
                    ))}
                </div>
            )}
            <ForgivenFlags userId={withdrawRequest.user_id} signals={pardoned} />
            <SuppressedFlags signals={triage?.signals ?? []} />
        </div>
    )
}

/** The evidence behind a flag, one click under its line.
 *
 *  Only for the flags whose sentence is a summary of a list: "killed bob 5
 *  times in a row" is the worst streak, and the reviewer deciding whether it
 *  was one evening of feeding or a rivalry over weeks needs every streak and
 *  when it happened. Closed by default so the case stays one line per finding.
 */
const SignalLog = ({ signal, userId, expanded = false }: { signal: any; userId: number; expanded?: boolean }) => {
    const d = signal.data ?? {}
    // Which streak's matches are open, as "victim:index". One at a time: two
    // session tables open at once is more than the case column can hold.
    const [open, setOpen] = useState<string | null>(null)
    const when = useStaffTime()
    const span = (began?: string | null, ended?: string | null) => {
        if (!began || !ended) return ''
        const minutes = Math.round((Date.parse(ended) - Date.parse(began)) / 60000)
        return minutes < 60 ? `${minutes} min` : `${(minutes / 60).toFixed(1)} h`
    }

    if (signal.key === 'repeat_victim' && d.victims?.length)
        return <StreakTable victims={d.victims} userId={userId} expanded={expanded} run={d.run ?? 3} />

    return null
}

/** Who was killed several spawns in a row, worst first, one row a player.
 *  Ten rows until asked for more; a player's streaks open under their row,
 *  biggest first. A list of every streak of every player was hundreds of
 *  lines nobody could read. */
const StreakTable = ({
    victims,
    userId,
    expanded,
    run,
}: {
    victims: any[]
    userId: number
    expanded: boolean
    run: number
}) => {
    const RUN = run
    const [all, setAll] = useState(false)
    const [openRow, setOpenRow] = useState<number | null>(null)
    const [openStreak, setOpenStreak] = useState<string | null>(null)
    const day = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '?')
    const time = (iso?: string | null) =>
        iso ? new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '?'
    const minutes = (a?: string | null, b?: string | null) =>
        a && b ? Math.max(1, Math.round((Date.parse(b) - Date.parse(a)) / 60000)) : null
    const sorted = [...victims].sort((a, b) => b.longest - a.longest || b.runs - a.runs)
    const rows = all ? sorted : sorted.slice(0, 10)
    const last = (v: any) =>
        (v.streaks ?? []).reduce((m: string | null, x: any) => (x.began && (!m || x.began > m) ? x.began : m), null)

    return (
        <details open={expanded} className='pt-0.5 text-[11px] font-normal text-white/60'>
            <summary className='cursor-pointer text-white/45 hover:text-white/80'>
                show the {victims.length} player{victims.length === 1 ? '' : 's'} they killed over and over
            </summary>
            <p className='pt-1 text-white/40'>
                Each row is a player this account kept killing: the player spawned, got killed by this account, spawned
                again, got killed again, with nobody else killing them in between. Many in a row on a fresh account can
                mean someone feeding kills.
            </p>
            <table className='mt-1 w-full max-w-xl tabular-nums'>
                <thead>
                    <tr className='text-left text-[10px] uppercase tracking-widest text-white/35'>
                        <th className='py-0.5 font-normal'>Player killed</th>
                        <th className='py-0.5 text-right font-normal'>Most times in a row</th>
                        <th className='py-0.5 text-right font-normal'>Streaks of {RUN}+</th>
                        <th className='py-0.5 text-right font-normal'>Last streak</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((v: any) => {
                        const isOpen = openRow === v.id
                        const streaks = [...(v.streaks ?? [])].sort((a: any, b: any) => b.kills - a.kills)
                        return (
                            <Fragment key={v.id}>
                                <tr className='border-t border-white/5'>
                                    <td className='py-0.5'>
                                        <a
                                            href={`/team-tools/accounts/users/${v.username}`}
                                            target='_blank'
                                            rel='noreferrer'
                                            className='underline decoration-dotted underline-offset-2 hover:text-white'
                                        >
                                            {v.username}
                                        </a>
                                    </td>
                                    <td
                                        className={`py-0.5 text-right ${
                                            v.longest >= 10 ? 'font-bold text-red-400' : v.longest >= 5 ? 'text-amber-300' : ''
                                        }`}
                                    >
                                        {v.longest} kills
                                    </td>
                                    <td className='py-0.5 text-right'>{v.runs}</td>
                                    <td className='py-0.5 text-right text-white/40'>{day(v.last ?? last(v))}</td>
                                    <td className='py-0.5 pl-3 text-right'>
                                        <button
                                            type='button'
                                            onClick={() => setOpenRow(isOpen ? null : v.id)}
                                            className='underline decoration-dotted underline-offset-2 hover:text-white'
                                        >
                                            {isOpen ? 'hide streaks' : 'see each streak'}
                                        </button>
                                    </td>
                                </tr>
                                {isOpen && (
                                    <tr>
                                        <td colSpan={5} className='pb-1 pl-3 text-white/45'>
                                            {v.runs > streaks.length && (
                                                <div className='text-white/30'>
                                                    showing the latest {streaks.length} of {v.runs} streaks, biggest first
                                                </div>
                                            )}
                                            {streaks.map((streak: any, i: number) => {
                                                const id = `${v.id}:${i}`
                                                const mins = minutes(streak.began, streak.ended)
                                                return (
                                                    <div key={i}>
                                                        Killed {streak.kills} times in a row · started {time(streak.began)}
                                                        {mins ? ` · lasted ${mins} min` : ''}
                                                        {streak.began && streak.ended && (
                                                            <button
                                                                type='button'
                                                                onClick={() => setOpenStreak(openStreak === id ? null : id)}
                                                                className='ml-2 underline decoration-dotted underline-offset-2 hover:text-white'
                                                            >
                                                                {openStreak === id ? 'hide the matches' : 'view the matches'}
                                                            </button>
                                                        )}
                                                        {openStreak === id && (
                                                            <StreakSessions
                                                                killerId={userId}
                                                                victimId={v.id}
                                                                began={streak.began}
                                                                ended={streak.ended}
                                                            />
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        )
                    })}
                </tbody>
            </table>
            {sorted.length > 10 && (
                <button
                    type='button'
                    onClick={() => setAll(!all)}
                    className='mt-1 underline decoration-dotted underline-offset-2 hover:text-white'
                >
                    {all ? 'show only the worst 10' : `show all ${sorted.length} players`}
                </button>
            )}
        </details>
    )
}

/** Let one flag go, from the line that raised it.
 *
 *  Put HERE, beside the finding, rather than in a settings screen somewhere:
 *  the decision is about this sentence, and a reviewer who has just read it is
 *  the person qualified to make it. A list of check names in a different place
 *  would be a list of things nobody can judge out of context.
 *
 *  A REASON IS REQUIRED. Six months from now the only thing standing between
 *  this and "why does this account sail through every check" is what somebody
 *  typed here, so there is no way to do it without saying why.
 *
 *  The current reading is sent with it, so the forgiveness can lapse when the
 *  finding grows -- see `bank.models.flag_forgiveness`. It is taken from the
 *  row on screen rather than measured again on the server, so what gets pinned
 *  is what the reviewer was actually looking at.
 */
/** The checks that are about a shared machine, for the wording of a lapsed
 *  dismissal: "found sharing the same machine ID". */
const MACHINE_SIGNALS = ['victim_device_cluster', 'same_device_kills', 'shared_machine', 'shared_device', 'device_twin']

/** "New account X found sharing the same machine ID", when this flag was
 *  dismissed and came back because somebody new turned up -- that is the
 *  news, so it is the line. Null otherwise. */
const newcomerLine = (signal: any): string | null => {
    const names: string[] = signal?.forgiven && !signal.forgiven.applies ? signal.forgiven.arrived_names ?? [] : []
    if (!names.length) return null
    const shown = names.slice(0, 5).join(', ') + (names.length > 5 ? ` and ${names.length - 5} more` : '')
    if (MACHINE_SIGNALS.includes(signal.key))
        return `New ${names.length === 1 ? 'account' : 'accounts'} ${shown} found sharing the same machine ID`
    return `Dismissed before, back because ${names.length === 1 ? 'a new account' : `${names.length} new accounts`} turned up: ${shown}`
}

/** Accounts that turned up after a dismissal on any of `keys`: the ones to
 *  mark NEW in the evidence tables. */
const arrivedOn = (signals: any[], keys: string[]) => {
    const ids = new Set<number>()
    const names = new Set<string>()
    for (const signal of signals ?? []) {
        if (!keys.includes(signal.key) || !signal.forgiven || signal.forgiven.applies) continue
        for (const id of signal.forgiven.arrived_ids ?? []) ids.add(id)
        for (const name of signal.forgiven.arrived_names ?? []) names.add(name)
    }
    return { ids, names }
}

const ForgiveFlag = ({
    userId,
    signal,
    onChanged,
}: {
    userId: number
    signal: any
    onChanged?: () => void
}) => {
    const [open, setOpen] = useState(false)
    const forgive = useForgiveFlag()
    const pardon = signal.forgiven

    /* Already forgiven, and the finding has since outgrown it or the date
     * passed. Not an action -- it is the explanation for why a flag somebody
     * cleared is back, which is the question this would otherwise raise. */
    if (pardon && !pardon.applies)
        return (
            <span className='block pt-0.5 text-[11px] font-normal text-white/45'>
                {!pardon.outgrown
                    ? `Dismissed by ${pardon.by ?? 'a reviewer'}, and that lapsed on the date they set.`
                    : (pardon.arrived_names ?? []).length
                      ? `${headlineOf(signal.text)}. Dismissed by ${pardon.by ?? 'a reviewer'} before ${pardon.arrived_names.length === 1 ? 'this account' : 'these accounts'} appeared, so it counts again.`
                      : pardon.arrived
                      ? `Dismissed by ${pardon.by ?? 'a reviewer'} for ${pardon.covered_count} ${pardon.covered_count === 1 ? 'account' : 'accounts'}; ${pardon.arrived} new ${pardon.arrived === 1 ? 'one has' : 'ones have'} appeared since, so it counts again.`
                      : `Dismissed by ${pardon.by ?? 'a reviewer'} when this read ${pardon.was}; it reads ${pardon.now} now, so it counts again.`}
            </span>
        )

    if (forgive.isLoading)
        return <span className='block pt-0.5 text-[11px] font-normal text-white/40'>saving…</span>

    if (!open)
        return (
            <button
                type='button'
                onClick={() => setOpen(true)}
                title='Dismiss this flag with a reason. It covers exactly the accounts named here — if a new one appears, the flag comes back. The check keeps running either way.'
                className='ml-2 align-baseline text-[11px] font-normal text-white/40 underline hover:text-white'
            >
                dismiss
            </button>
        )

    // The same confirm bar and required reason as declining a withdrawal.
    return (
        <span className='mt-1 block font-normal'>
            <ConfirmBar
                message='Dismiss this flag?'
                detail={headlineOf(signal.text)}
                confirmLabel='Dismiss'
                note='Why is this one fine?'
                noteRequired
                notePlaceholder='e.g. two brothers on one connection, confirmed on a call'
                busy={forgive.isLoading}
                onConfirm={(_choice, note) =>
                    forgive.mutate(
                        {
                            user_id: userId,
                            signal_key: signal.key,
                            reason: (note ?? '').trim(),
                            covered: membersOf(signal),
                            magnitude: magnitudeOf(signal),
                        },
                        {
                            onSuccess: () => {
                                setOpen(false)
                                onChanged?.()
                            },
                        }
                    )
                }
                onCancel={() => setOpen(false)}
            />
            {forgive.error && (
                <span className='text-[11px] font-normal text-red-400'>
                    {(forgive.error as any)?.error?.message ?? 'That was refused.'}
                </span>
            )}
        </span>
    )
}

/** WHO this finding names, matching `MEMBERS` in `withdraw_triage`.
 *
 *  A forgiveness covers these exact accounts, addresses, machines or clusters
 *  -- not "two of them". Two accounts on an address can become two completely
 *  different accounts with the count never moving, and a forgiveness that read
 *  the count would silently cover people nobody ever looked at.
 *
 *  Duplicated in the browser on purpose and kept deliberately small. What gets
 *  pinned has to be who the reviewer was looking at, and the only place that
 *  list exists at the moment they decide is this page -- asking the server
 *  would re-run a triage that takes seconds and could answer differently.
 */
const membersOf = (signal: any): string[] | null => {
    const d = signal.data ?? {}
    const users = (ids: any[]) => (ids ?? []).map((i: any) => `u:${i}`)
    const victims = () =>
        (d.clusters ?? []).flatMap((g: any) => (g.victims ?? []).map((v: any) => `u:${v.id}`))
    const sorted = (xs: string[]) => [...new Set(xs)].sort()
    switch (signal.key) {
        case 'shared_identity':
            return sorted([
                ...users(d.payout_twins),
                ...users(d.wallet_history),
                ...users(d.ip_twins),
                ...users(d.ip_neighbours),
            ])
        case 'shared_device':
            return sorted([...users(d.device_twins), ...users(d.device_neighbours)])
        case 'same_ip_kills':
        case 'same_device_kills':
            return sorted((d.opponents ?? []).map((o: any) => `u:${o.id}`))
        case 'victim_ip_cluster':
            return sorted([
                ...(d.clusters ?? []).map((g: any) => `ip:${g.ip}`),
                ...victims(),
            ])
        case 'victim_device_cluster':
            return sorted([
                ...(d.clusters ?? []).map((g: any) => `hw:${g.hwid}`),
                ...victims(),
            ])
        case 'cluster':
            return sorted((d.clusters ?? []).map((g: any) => `c:${g.id}`))
        case 'repeat_victim':
            return sorted((d.victims ?? []).map((v: any) => `u:${v.id}`))
        default:
            return null
    }
}

/** How big it is, for the two checks that name nobody. */
const magnitudeOf = (signal: any): number | null => {
    const d = signal.data ?? {}
    switch (signal.key) {
        case 'shared_machine':
            return Number(d.accounts ?? 0)
        // Both block Approve since D-040, so a dismissal has to lapse when
        // the finding grows: more unexplained money, or a bigger bonus.
        case 'net_position':
            return Number(d.unexplained ?? 0)
        case 'bonus_unwagered':
            return Number(d.granted ?? 0)
        case 'cheat_history':
            return (
                Number(d.reports_actioned ?? 0) +
                Number(d.reports_open ?? 0) +
                Number(d.cheat_sessions ?? 0)
            )
        default:
            return null
    }
}

/** Checks that fired but were not counted because the account is only taking
 *  back money it deposited (D-041). Said rather than silently dropped: a flag
 *  that vanished with no explanation reads as "nothing was found", which is
 *  not what happened. */
const SuppressedFlags = ({ signals }: { signals: any[] }) => {
    const suppressed = signals.filter((signal: any) => signal.suppressed_because)
    if (!suppressed.length) return null
    return (
        <div className='flex flex-col gap-1 border-t border-white/10 pt-2 text-[11px] text-white/45'>
            <span className='text-[10px] uppercase tracking-widest text-white/35'>
                Not counted &mdash; suppressed
            </span>
            {suppressed.map((signal: any) => (
                <span key={signal.key} className='flex flex-row flex-wrap items-baseline gap-2'>
                    <span className='text-white/60'>{headlineOf(signal.text)}</span>
                    <span>&mdash; suppressed because {signal.suppressed_because}</span>
                </span>
            ))}
        </div>
    )
}

/** The flags somebody has already cleared on this account.
 *
 *  Its own small block under the case, never folded into "what argues for
 *  paying": a forgiven flag is not evidence the account is fine, it is a
 *  record that a human judged one awkward fact and moved on. Printing it as an
 *  argument would turn one reviewer's note into a second opinion.
 */
const ForgivenFlags = ({
    userId,
    signals,
    onChanged,
}: {
    userId: number
    signals: any[]
    onChanged?: () => void
}) => {
    const unforgive = useUnforgiveFlag()
    if (!signals.length) return null
    return (
        <div className='flex flex-col gap-1 border-t border-white/10 pt-2 text-[11px] text-white/45'>
            <span className='text-[10px] uppercase tracking-widest text-white/35'>
                Dismissed &mdash; not holding this payout
            </span>
            {signals.map((signal: any) => (
                <span key={signal.key} className='flex flex-row flex-wrap items-baseline gap-2'>
                    <span className='text-white/60'>{headlineOf(signal.text)}</span>
                    <span>
                        &mdash; {signal.forgiven.reason}
                        {signal.forgiven.by ? ` (${signal.forgiven.by})` : ''}
                    </span>
                    <button
                        type='button'
                        disabled={unforgive.isLoading}
                        onClick={() =>
                            unforgive.mutate(
                                { user_id: userId, signal_key: signal.key },
                                { onSuccess: () => onChanged?.() }
                            )
                        }
                        title='Put this flag back. It will count towards the verdict again.'
                        className='text-white/35 underline hover:text-white disabled:opacity-30'
                    >
                        put it back
                    </button>
                </span>
            ))}
        </div>
    )
}

/** What people call the currency, rather than the key the icon list uses.
 *  "41 usdc-spl" is a database value that escaped onto a payments screen. */
const currencyLabel = (currency: string) =>
    currencies.find((c) => c.name === currency)?.label ?? currency.toUpperCase()

/**
 * One figure that matters, and the figures it is made of.
 *
 * Four tiles of identical weight made the reader do the arithmetic to find
 * out which was the total -- 88.08, 30.00, 4.25 and 122.33 are the same size,
 * the same colour and in the same kind of box, so nothing says the last one
 * contains the other three. A sum and its addends are not siblings.
 *
 * The bar is the point of it: the split between money taken off other players
 * and money BR1 put up is the thing a reviewer is weighing, and a bar answers
 * that before any number is read.
 */
/** Which ledger rows a figure is made of: `bank.transactions` filters, and
 *  rows to leave out. Each matches its figure's own definition in
 *  `withdraw_triage` (`_ledger`, `_house_subsidy`), so clicking a number
 *  lists exactly the rows that add up to it. */
type LedgerQuery = {
    title: string
    filters: Record<string, any>
    exclude?: Record<string, any>[]
}

// `Transaction.OPERATIONS`: DEPOSIT 1, AIRDROP 3, EARNING 6, LOOT_EARNING 61,
// RACE_EARNING 7, TREASURE_HUNT_EARNING 8, KILL_RACE_EARNING 10,
// REFERRAL_REWARD 11.
// What an airdrop was for, read from its note -- automatic ("Level 5 Reward:
// Airdrop 1", "deposit match <uuid>") or typed by hand ("Level 63 reward",
// "NIGHTSEAWOLF - Depo Match"). The same patterns as the airdrop chart's totals
// (`AIRDROP_LEVEL_RE` / `AIRDROP_MATCH_RE` in withdraw_triage), so a slice and
// the rows it opens agree.
const AIRDROP_KIND = {
    level: '^\\s*level\\s*[0-9]+\\s*reward',
    match: '(match.*dep|dep.*match)',
}
const LEVEL_UP = { comment__iregex: AIRDROP_KIND.level }
const DEPOSIT_MATCH = { comment__iregex: AIRDROP_KIND.match }
const LEDGER: Record<string, LedgerQuery> = {
    deposits: { title: 'Deposits', filters: { operation: 1 } },
    airdrops: { title: 'Airdrops (USDC and credit)', filters: { operation: 3 } },
    usdcAirdrops: { title: 'USDC airdrops', filters: { operation: 3, currency__symbol: 'USDC' } },
    levelUps: {
        title: 'Level-up rewards',
        filters: { operation: 3, currency__symbol: 'AIRDROP', amount__gt: 0, ...LEVEL_UP },
    },
    depositMatch: {
        title: 'Deposit match',
        filters: { operation: 3, currency__symbol: 'AIRDROP', amount__gt: 0, ...DEPOSIT_MATCH },
        exclude: [LEVEL_UP],
    },
    // A person's id is positive; the system account is -1.
    manualAirdrops: {
        title: 'Manual airdrops (credit, given by staff)',
        filters: { operation: 3, currency__symbol: 'AIRDROP', amount__gt: 0, created_by_id__gt: 0 },
        exclude: [LEVEL_UP, DEPOSIT_MATCH],
    },
    otherAirdrops: {
        title: 'Other airdrops (credit, automatic)',
        filters: { operation: 3, currency__symbol: 'AIRDROP', amount__gt: 0 },
        exclude: [LEVEL_UP, DEPOSIT_MATCH, { created_by_id__gt: 0 }],
    },
    kills: { title: 'Earned from kills', filters: { operation: 6, currency__symbol: 'USDC' } },
    killRaces: { title: 'Kill race prizes', filters: { operation: 10, currency__symbol: 'USDC' } },
    otherWinnings: {
        title: 'Other winnings',
        filters: { operation__in: [61, 7, 8, 11], currency__symbol: 'USDC' },
    },
}

/** The rows behind a funding figure.
 *
 *  "Airdropped $245.26" is a number somebody then has to go and verify
 *  somewhere else: which promotion, how many grants, who approved them, was it
 *  one payment or forty. The ledger rows answer all of that and were two
 *  screens away, so in practice nobody looked and the figure was taken on
 *  trust -- on the screen where a payout is approved.
 *
 *  Opened from the figure itself, because that is where the question occurs.
 */
const FundingLog = ({
    userId,
    query,
    onClose,
}: {
    userId: number
    query: LedgerQuery
    onClose: () => void
}) => {
    const when = useStaffTime()
    const [rows, setRows] = useState<any[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [count, setCount] = useState(0)
    // Where the next page starts, and the newest row when the first was read,
    // so later pages read the same snapshot (see `bank.transactions`).
    const [next, setNext] = useState<{ offset: number; maxId: number | null }>({ offset: 0, maxId: null })
    const [loadingMore, setLoadingMore] = useState(false)
    // Airdrop rows lead with what kind they were.
    const airdrops = query.filters.operation === 3 || (query.filters.operation__in ?? []).includes(3)
    const title = query.title

    const fetchPage = (offset: number, maxId: number | null) =>
        jsonrpc('bank.transactions', {
            filters: { user: String(userId), ...query.filters },
            exclude: query.exclude ?? [],
            offset,
            ...(maxId !== null ? { max_id: maxId } : {}),
        })

    useEffect(() => {
        let live = true
        fetchPage(0, null)
            .then((r: any) => {
                if (!live) return
                setRows(r?.result?.data ?? [])
                setCount(r?.result?.count ?? 0)
                setNext({ offset: r?.result?.offset ?? 0, maxId: r?.result?.max_id ?? null })
            })
            .catch((e: any) => live && setError(e?.message ?? 'Could not load the ledger'))
        return () => {
            live = false
        }
    }, [userId, query])

    const more = () => {
        setLoadingMore(true)
        fetchPage(next.offset, next.maxId)
            .then((r: any) => {
                setRows((prev) => [...(prev ?? []), ...(r?.result?.data ?? [])])
                setNext({ offset: r?.result?.offset ?? next.offset, maxId: next.maxId })
            })
            .catch((e: any) => setError(e?.message ?? 'Could not load the ledger'))
            .finally(() => setLoadingMore(false))
    }

    useEffect(() => {
        const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
        window.addEventListener('keydown', key)
        return () => window.removeEventListener('keydown', key)
    }, [onClose])

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm'
            onMouseDown={onClose}
        >
            <div
                onMouseDown={(e) => e.stopPropagation()}
                className='flex max-h-full w-full max-w-3xl flex-col gap-3 overflow-y-auto rounded-lg border border-white/15 bg-neutral-950 p-5 text-xs'
            >
                <header className='flex flex-row items-baseline gap-3 border-b border-white/10 pb-2'>
                    <h2 className='text-sm font-bold uppercase tracking-widest'>{title}</h2>
                    {rows && (
                        <span className='text-white/40'>
                            {count} {count === 1 ? 'row' : 'rows'}
                            {count > rows.length && `, showing the latest ${rows.length}`}
                        </span>
                    )}
                    <button
                        type='button'
                        onClick={onClose}
                        className='ml-auto text-white/40 hover:text-white'
                    >
                        close
                    </button>
                </header>

                {error && <span className='text-red-400'>{error}</span>}
                {!rows && !error && <span className='text-white/40'>Loading…</span>}
                {rows?.length === 0 && <span className='text-white/40'>No rows of this kind.</span>}

                {rows && rows.length > 0 && (
                    <div
                        className={`grid ${
                            airdrops
                                ? 'grid-cols-[max-content,max-content,max-content,max-content,minmax(0,1fr)]'
                                : 'grid-cols-[max-content,max-content,max-content,minmax(0,1fr)]'
                        } gap-x-4 gap-y-1`}
                    >
                        {[...(airdrops ? ['Type'] : []), 'Amount', 'Who did it', 'When', 'Note'].map((h) => (
                            <div
                                key={h}
                                className='text-[10px] uppercase tracking-widest text-white/40'
                            >
                                {h}
                            </div>
                        ))}
                        {rows.map((row: any) => (
                            <Fragment key={row.id}>
                                {airdrops && (
                                    <div className='whitespace-nowrap text-white'>{airdropType(row.comment)}</div>
                                )}
                                <div className='whitespace-nowrap tabular-nums text-white'>
                                    {money(row.amount)} {row.currency_symbol}
                                </div>
                                {/* Who granted it. On an airdrop this is the
                                    whole question -- a promotion the system ran
                                    and a one-off a person typed in look
                                    identical in the total. */}
                                <div className='whitespace-nowrap text-white/60'>
                                    {row.initiator ?? '—'}
                                </div>
                                <div className='whitespace-nowrap text-white/60'>
                                    {when(row.created_at)}
                                </div>
                                <div className='truncate text-white/40' title={row.comment ?? ''}>
                                    {row.comment || '—'}
                                </div>
                            </Fragment>
                        ))}
                    </div>
                )}
                {rows && rows.length < count && (
                    <button
                        type='button'
                        disabled={loadingMore}
                        onClick={more}
                        className='self-center rounded border border-white/20 px-3 py-1 text-[11px] uppercase tracking-widest text-white/60 hover:border-white/50 hover:text-white disabled:opacity-40'
                    >
                        {loadingMore ? 'Loading…' : `Show ${Math.min(20, count - rows.length)} more`}
                    </button>
                )}
            </div>
        </div>
    )
}

/** What an airdrop was for, read off its ledger comment -- the same test the
 *  airdrop pie uses (`_house_subsidy`): progression writes "Level N Reward:
 *  ...", the deposit match writes "deposit match <uuid>". */
const airdropType = (comment?: string | null) => {
    const text = comment ?? ''
    if (new RegExp(AIRDROP_KIND.level, 'i').test(text)) return 'Level-up reward'
    if (new RegExp(AIRDROP_KIND.match, 'i').test(text)) return 'Deposit match'
    return 'Other'
}

const TotalWithParts = ({
    total,
    label,
    hint,
    parts,
    bar = true,
}: {
    total: string
    label: string
    hint?: string
    parts: Array<{
        label: string
        value: string
        display?: string
        tone: string
        hint?: string
        /** Opens the rows behind this figure. Absent where there are none
         *  to show, so nothing looks clickable that is not. */
        onOpen?: () => void
    }>
    /** False where the parts are separate counts rather than a split of the
     *  total, and a bar would assert an arithmetic that does not hold. */
    bar?: boolean
}) => {
    const sum = parts.reduce((n, part) => n + Number(part.value || 0), 0)
    const share = (value: string) => (sum > 0 ? (Number(value || 0) / sum) * 100 : 0)

    return (
        <div className='flex h-full flex-row items-center justify-between gap-4 rounded-md border border-white/15 bg-white/[0.05] px-3 py-3'>
            <div className='flex min-w-0 flex-col gap-1'>
            <span
                className={twMerge(
                    'flex flex-row items-center gap-1 text-[11px] uppercase tracking-wide text-white/65',
                    hint && 'cursor-help hover:text-white'
                )}
                title={hint}
            >
                {label}
                {hint && <FontAwesomeIcon icon={faCircleInfo} className='opacity-50' />}
            </span>

            <div className='flex flex-col gap-1'>
                {parts.map((part) => (
                    <span key={part.label} className='flex flex-row items-center gap-2'>
                        {bar && (
                            <span
                                className='h-2 w-2 shrink-0 rounded-full'
                                style={{ backgroundColor: part.tone }}
                            />
                        )}
                        <span
                            className={twMerge(
                                'flex flex-row items-center gap-1 text-[11px] font-normal uppercase tracking-wide text-white/65',
                                part.hint && 'cursor-help hover:text-white'
                            )}
                            title={part.hint}
                        >
                            {part.label}
                            {part.hint && <FontAwesomeIcon icon={faCircleInfo} className='opacity-50' />}
                        </span>
                        {/* The figure itself opens the rows behind it, because
                            that is where the question occurs -- not a separate
                            "view ledger" control somewhere else on the page. */}
                        {part.onOpen ? (
                            <button
                                type='button'
                                onClick={part.onOpen}
                                title='Show the transactions behind this'
                                className='text-sm underline decoration-dotted underline-offset-2 hover:text-white'
                            >
                                {part.display ?? part.value}
                            </button>
                        ) : (
                            <span className='text-sm'>{part.display ?? part.value}</span>
                        )}
                    </span>
                ))}
            </div>
            </div>

            {/* On the right, and big enough to read the split off. */}
            {bar && sum > 0 ? (
                <Donut parts={parts} total={total} />
            ) : (
                <span className='shrink-0 text-3xl font-bold tracking-tight text-white'>{total}</span>
            )}
        </div>
    )
}

/**
 * A breakdown as a ring, with the total in the hole.
 *
 * Small and read at a glance rather than measured: the question is "how much
 * of this came from playing", not "is it 61.4% or 63.1%". Every slice is also
 * named and given its number in the legend beside it, so identity never rests
 * on colour alone -- which is what makes the ring safe to keep this small.
 *
 * The 2px gaps are surface-coloured, not strokes: a stroke would eat into the
 * arc and mis-state the share it draws.
 *
 * Colours are the validated categorical order for this dark surface -- every
 * adjacent pair clears CVD separation, the lightness band and 3:1 contrast.
 * They are assigned in fixed order and never cycled, so a slice that
 * disappears cannot repaint the ones that remain.
 */
/**
 * A breakdown as a ring, with the total in the hole.
 *
 * Drawn on a viewBox twice the rendered size. An arc built out of a dash
 * pattern is antialiased against the coordinate grid, so at 1:1 the edge of
 * each slice lands between pixels and reads as ragged; at 2x the browser
 * downsamples a denser curve and the edge comes out clean.
 *
 * The hole is sized off the stroke, not guessed: the total sits inside it, so
 * the ring has to leave more room than the text needs rather than as much.
 *
 * Every slice is also named and given its number in the legend beside it, so
 * identity never rests on colour alone. The gaps are cut out of the arc
 * rather than stroked over it -- a stroked divider would eat into the arc and
 * mis-state the share it draws.
 */
const DONUT_SIZE = 148
const DONUT_VB = DONUT_SIZE * 2
const DONUT_MID = DONUT_VB / 2
const DONUT_W = 34
const DONUT_R = DONUT_MID - DONUT_W / 2 - 6
const DONUT_C = 2 * Math.PI * DONUT_R
const DONUT_GAP = 5

const Donut = ({
    parts,
    total,
}: {
    parts: Array<{ label: string; value: string; display?: string; tone: string }>
    total: string
}) => {
    const values = parts.map((part) => Math.max(Number(part.value || 0), 0))
    const sum = values.reduce((a, b) => a + b, 0)
    /* Only the slices that have something in them.
     *
     * A part worth 0.00 still took a gap out of the ring, which drew a notch
     * with nothing either side of it -- the "pixely" break in a ring that is
     * really one solid colour. And a slice worth a fraction of a percent came
     * out as a one-pixel sliver, which is the other thing that reads as
     * rendering noise rather than as data. Below half a percent it cannot be
     * read as a share anyway; it stays in the legend, where it has a number.
     */
    const drawn = parts
        .map((part, i) => ({ part, share: sum > 0 ? values[i] / sum : 0 }))
        .filter((slice) => slice.share >= 0.005)
    // No divider when there is nothing to divide from.
    const gap = drawn.length > 1 ? DONUT_GAP : 0
    let offset = 0

    return (
        <span
            className='relative inline-block shrink-0 leading-none'
            style={{ width: DONUT_SIZE, height: DONUT_SIZE }}
        >
            <svg
                width={DONUT_SIZE}
                height={DONUT_SIZE}
                viewBox={`0 0 ${DONUT_VB} ${DONUT_VB}`}
                className='block'
                shapeRendering='geometricPrecision'
                role='img'
                aria-label={`${total} total`}
            >
                <circle
                    cx={DONUT_MID}
                    cy={DONUT_MID}
                    r={DONUT_R}
                    fill='none'
                    stroke='rgba(255,255,255,0.08)'
                    strokeWidth={DONUT_W}
                />
                {drawn.map(({ part, share }) => {
                    const len = Math.max(share * DONUT_C - gap, 0)
                    const rotation = (offset / DONUT_C) * 360 - 90
                    offset += share * DONUT_C
                    if (len <= 0) return null
                    return (
                        <circle
                            key={part.label}
                            cx={DONUT_MID}
                            cy={DONUT_MID}
                            r={DONUT_R}
                            fill='none'
                            stroke={part.tone}
                            strokeWidth={DONUT_W}
                            strokeLinecap='butt'
                            strokeDasharray={`${len} ${DONUT_C - len}`}
                            transform={`rotate(${rotation} ${DONUT_MID} ${DONUT_MID})`}
                        >
                            <title>{`${part.label}: ${part.display ?? part.value}`}</title>
                        </circle>
                    )
                })}
            </svg>
            {/* Inside the hole, not over the ring: the box is inset by the
                stroke plus a margin, so a long total wraps rather than
                running out across the colour. */}
            <span
                className={`absolute flex items-center justify-center text-center font-bold leading-tight tracking-tight text-white ${
                    // A number does not wrap, so a long one steps down a size
                    // to stay inside the hole.
                    total.length <= 8 ? 'text-base' : total.length <= 10 ? 'text-sm' : 'text-xs'
                }`}
                style={{ inset: (DONUT_W / 2 + 14) / 2 }}
            >
                {total}
            </span>
        </span>
    )
}

/**
 * The addresses this account plays from, and who else is on each.
 *
 * It read "4 other accounts on their IP address" as a tile, which is a count
 * of something a reviewer then had to go elsewhere to see. WHICH address is
 * the difference between a household and a ring, and the names on it are what
 * turns the finding into a decision -- so the addresses are listed, each with
 * the accounts that share it, and nothing has to be opened to read it.
 */
const SharedList = ({
    title,
    empty,
    rows,
    tone,
}: {
    title: string
    empty: string
    tone: string
    rows: Array<{ key: string; shared: string[] }>
}) => (
    <div className='col-span-full flex flex-col gap-1'>
        <span className='text-[11px] font-bold uppercase tracking-widest text-white/65'>{title}</span>
        {rows.length === 0 ? (
            <span className='font-normal text-white/40'>{empty}</span>
        ) : (
            rows.map((row) => (
                <div
                    key={row.key}
                    className={twMerge(
                        'flex flex-row flex-wrap items-baseline gap-x-3 rounded border px-2 py-1',
                        row.shared.length ? tone : 'border-white/10'
                    )}
                >
                    <span className='font-mono'>{row.key}</span>
                    {row.shared.length === 0 ? (
                        <span className='font-normal text-white/40'>theirs alone</span>
                    ) : (
                        <span className='font-normal text-white/70'>
                            also used by{' '}
                            {row.shared.map((who, i) => (
                                <Fragment key={who}>
                                    {i > 0 && ', '}
                                    <a
                                        className='underline'
                                        href={`/team-tools/accounts/users/${who}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {who}
                                    </a>
                                </Fragment>
                            ))}
                        </span>
                    )}
                </div>
            ))
        )}
    </div>
)

/** IP -> the accounts sharing it, inverted from neighbour -> addresses. */
const addressRows = (facts: any) => {
    const byAddress: Record<string, string[]> = {}
    Object.entries(facts.ip_neighbour_addresses ?? {}).forEach(([who, ips]) => {
        ;(ips as string[]).forEach((ip) => {
            byAddress[ip] = [...(byAddress[ip] ?? []), who]
        })
    })
    return Object.entries(byAddress).map(([key, shared]) => ({ key, shared }))
}

/**
 * A finding, trimmed to the finding.
 *
 * The checks write one line each and put their whole working in it -- the
 * names, the kill split each way, the totals. That is the right material and
 * the wrong place: this column answers "what is wrong", and the evidence for
 * it is below under Suspicious activity, where it has a fold of its own.
 *
 * Cut at the colon or the dash the checks use to introduce their detail. The
 * full text stays on the hover, so nothing is lost, only moved.
 */
const headlineOf = (text: string) => {
    // The cheat after the colon IS the finding; nothing to trim.
    if (/^Cheating confirmed/.test(text)) return text
    const cut = text.search(/\s--\s|:\s/)
    return cut > 0 ? text.slice(0, cut).replace(/[\s:,-]+$/, '') : text
}

/** The payout block as one short warning: each reason's headline, and a
 *  confirmed cheat kept whole (see `headlineOf`), so it names the cheat. */
const blockWarning = (reasons: string[]) =>
    reasons
        .map((reason) => headlineOf(reason))
        .map((reason) => (reason.endsWith('.') ? reason : `${reason}.`))
        .join(' ')

/**
 * One reading, on the scale the check judges it on.
 *
 * A meter rather than a bar chart: there is one value and one threshold, and
 * what a reviewer needs is not the number but where it sits relative to the
 * line. "9 opponents" is meaningless until you know the check wants ten, and
 * nobody holds that in their head across a queue.
 *
 * The line is drawn from the server's own setting rather than repeated here,
 * so somebody retuning the check in the admin moves this mark with it.
 */
const Gauge = ({
    label,
    value,
    mark,
    max,
    markLabel,
    bad,
    display,
    hint,
}: {
    label: string
    value: number
    mark: number
    max: number
    markLabel: string
    bad: boolean
    display: string
    hint?: string
}) => {
    const pct = (n: number) => `${Math.min(Math.max((n / Math.max(max, 1)) * 100, 0), 100)}%`
    return (
        <div className='flex flex-col gap-1'>
            <span className='flex flex-row items-baseline gap-2'>
                <span className={twMerge('text-sm font-bold', bad ? 'text-amber-300' : 'text-white')}>
                    {display}
                </span>
                <span
                    className={twMerge(
                        'flex flex-row items-center gap-1 text-[10px] uppercase tracking-widest text-white/50',
                        hint && 'cursor-help hover:text-white'
                    )}
                    title={hint}
                >
                    {label}
                    {hint && <FontAwesomeIcon icon={faCircleInfo} className='opacity-50' />}
                </span>
            </span>
            <span className='relative block h-1.5 w-full rounded-full bg-white/10'>
                <span
                    className={twMerge(
                        'absolute inset-y-0 left-0 rounded-full',
                        bad ? 'bg-amber-400' : 'bg-sky-400'
                    )}
                    style={{ width: pct(value) }}
                />
                {/* The threshold, as a tick rather than a second bar: it is
                    not a quantity to compare against, it is a line. */}
                {mark > 0 && (
                    <span
                        className='absolute -top-0.5 h-2.5 w-px bg-white/70'
                        style={{ left: pct(mark) }}
                        title={`${markLabel} ${mark}`}
                    />
                )}
            </span>
            {/* The tick carries the comparison; the caption repeated it in
                words under every bar. Kept only where the mark is a person to
                compare with rather than a line somebody drew. */}
            {markLabel !== '' && mark > 0 && (
                <span className='text-[10px] text-white/30'>
                    {markLabel}: {max === 100 ? `${Math.round(mark)}%` : Math.round(mark)}
                </span>
            )}
        </div>
    )
}

/** One count, big enough to read across the room. */
const Stat = ({
    value,
    label,
    hint,
    tone,
}: {
    value: string
    label: string
    hint?: string
    tone?: string
}) => (
    <span className='flex flex-col items-center gap-0.5'>
        <span className={twMerge('text-2xl font-bold tracking-tight text-white', tone)}>{value}</span>
        <span
            className={twMerge(
                'flex flex-row items-center gap-1 text-[10px] uppercase tracking-widest text-white/50',
                hint && 'cursor-help hover:text-white'
            )}
            title={hint}
        >
            {label}
            {hint && <FontAwesomeIcon icon={faCircleInfo} className='opacity-50' />}
        </span>
    </span>
)

/**
 * Money in against money out, and who is up on it.
 *
 * Not a ring: these two are not parts of a whole, they are opposite
 * directions, and a pie of them would say something untrue about the shape of
 * it. Two bars on a shared scale answer the question a net figure alone
 * cannot -- whether it is -$30 out of forty dollars or out of four thousand.
 */
/** How much of the balance this payout is, said as a reading.
 *
 *  "72% of their balance" is a number the reviewer then has to judge. The
 *  question behind it is whether somebody is taking a slice or clearing the
 *  account out, and that is worth saying rather than leaving to be worked out
 *  on a screen people scan in seconds. The figure stays, because somebody
 *  checking the arithmetic should not have to take the sentence on trust.
 */
const shareOfBalance = (amount: number, available: number) => {
    if (available <= 0) return `they have nothing else in the account`
    const pct = Math.round((amount / available) * 100)
    const reading =
        pct >= 95
            ? 'emptying the account'
            : pct >= 60
              ? 'most of what they hold'
              : pct >= 25
                ? 'a good part of what they hold'
                : 'a small part of what they hold'
    return `${reading} — they have ${money(available.toFixed(2))}, so this is ${pct}%`
}

/** Did this event come from something the user was operating?
 *
 *  The request card opens on a click anywhere in it, which is what makes a box
 *  that looks like one thing behave like one thing. The cost is that every
 *  control INSIDE it -- approve, decline, the note and reason boxes, the
 *  account links -- also bubbles a click up to the card, so filling in a
 *  decline reason closed the case being declined.
 *
 *  Checked by walking up from the event target rather than by putting
 *  `stopPropagation` on each control: there are a dozen of them, and the next
 *  one added would have to remember.
 */
const fromControl = (target: EventTarget | null) =>
    target instanceof Element &&
    target.closest('input, textarea, select, button, a, label, [contenteditable]') !== null

/** What they own, counted.
 *
 *  Just the holdings. The cut BR1 keeps from them used to be reported here
 *  too, and it belongs with the money -- it is now one side of the
 *  made-or-lost figure above, where it can be read against what we paid out
 *  instead of sitting on its own as a percentage.
 *
 *  Owning nothing is the common case and reads as "0 NFTs" rather than an
 *  empty block, so the row means the same thing every time it is looked at.
 */
const NftsHeld = ({ facts }: { facts: any }) => {
    const held = Number(facts.nfts_held ?? 0)
    const byType: Record<string, number> = facts.nfts_by_type ?? {}
    const parts = Object.entries(byType)
        .filter(([, n]) => n > 0)
        .sort(([, a], [, b]) => b - a)

    return (
        <span className='flex flex-row flex-wrap items-baseline gap-x-2 text-white/65'>
            <span
                className={twMerge(
                    'text-sm font-bold',
                    held > 0 ? 'text-violet-300' : 'text-white/50'
                )}
            >
                {held} {held === 1 ? 'NFT' : 'NFTs'}
            </span>
            {parts.length > 0 && (
                <span className='text-white/50'>
                    {parts
                        .map(([kind, n]) => `${n} ${kind}${n === 1 ? '' : 's'}`)
                        .join(', ')}
                </span>
            )}
        </span>
    )
}

const Ledger = ({
    title,
    net,
    inLabel,
    inValue,
    outLabel,
    outValue,
    hint,
    inHint,
    outHint,
}: {
    title: string
    /** Positive is the subject of the title being up. */
    net: number
    inLabel: string
    inValue: number
    outLabel: string
    outValue: number
    hint?: string
    /** What each side is made of, where the label cannot say it. */
    inHint?: string
    outHint?: string
}) => {
    const scale = Math.max(inValue, outValue, 1)
    return (
        <div className='flex flex-col gap-1'>
            <span className='flex flex-row items-baseline gap-2'>
                <span
                    className={twMerge(
                        'text-xl font-bold tracking-tight',
                        net === 0 ? 'text-white/50' : net > 0 ? 'text-green-400' : 'text-red-400'
                    )}
                >
                    {net === 0 ? '' : net > 0 ? '+' : '\u2212'}${Math.abs(net).toFixed(2)}
                </span>
                <span
                    className={twMerge(
                        'flex flex-row items-center gap-1 text-[11px] uppercase tracking-wide text-white/65',
                        hint && 'cursor-help hover:text-white'
                    )}
                    title={hint}
                >
                    {title}
                    {hint && <FontAwesomeIcon icon={faCircleInfo} className='opacity-50' />}
                </span>
            </span>
            <div className='flex flex-col gap-1'>
                <Track label={inLabel} value={inValue} scale={scale} tone='bg-green-500' hint={inHint} />
                <Track label={outLabel} value={outValue} scale={scale} tone='bg-red-500' hint={outHint} />
            </div>
        </div>
    )
}

const Track = ({
    label,
    value,
    scale,
    tone,
    hint,
}: {
    label: string
    value: number
    scale: number
    tone: string
    hint?: string
}) => (
    <span className='flex flex-row items-center gap-2'>
        <span
            className={twMerge(
                'flex w-28 shrink-0 flex-row items-center gap-1 text-[10px] uppercase tracking-widest text-white/45',
                hint && 'cursor-help hover:text-white'
            )}
            title={hint}
        >
            {label}
            {hint && <FontAwesomeIcon icon={faCircleInfo} className='opacity-50' />}
        </span>
        <span className='h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10'>
            <span
                className={twMerge('block h-full rounded-full', tone)}
                style={{ width: `${Math.min((value / scale) * 100, 100)}%` }}
            />
        </span>
        <span className='w-20 shrink-0 text-right text-xs tabular-nums'>${value.toFixed(2)}</span>
    </span>
)

const CaseColumn = ({
    title,
    icon,
    tone,
    children,
}: {
    title: string
    icon: any
    tone: string
    children: React.ReactNode
}) => (
    <div className='flex flex-col gap-1.5'>
        <span className={twMerge('flex flex-row items-center gap-2 text-xs font-bold', tone)}>
            <FontAwesomeIcon icon={icon} className='opacity-70' />
            {title}
        </span>
        {/* Bulleted and given room. These are separate findings, and run
            together at 10px they read as one long sentence nobody finishes. */}
        <ul className='ml-1 flex list-disc flex-col gap-1.5 pl-4 text-xs font-normal leading-relaxed marker:text-white/25'>
            {children}
        </ul>
    </div>
)

const WithdrawRequestAdminTemplate = () => {
    const { withdrawRequest, details, showDetails } = useContext(WithdrawRequestContext)
    const when = useStaffTime()
    const face = withdrawRequest.triage ? VERDICT_FACE[withdrawRequest.triage.verdict] : null
    const { setFilters } = useContext(WithdrawRequestListContext)
    const approvable = withdrawRequest.status === 'requested'
    const revokeable = withdrawRequest.status === 'approved'
    const approve = withdrawRequest.status_history.find((t) => t.status === 'approved')
    const processable = withdrawRequest.status === 'approved'
    const not_finalized = withdrawRequest.status === 'processing'

    const payoutError = withdrawRequest.payout_error

    const filterHistory = () => {
        setFilters((prev) => ({ ...prev, username: withdrawRequest.created_by }))
    }
    return (
        // Requests ran together as one continuous strip, so the eye had to
        // find where each began. A card each, with space between them -- and
        // the card is what opens it, because a box that looks like one thing
        // should not have one strip inside it that is the only live part.
        <div
            role='button'
            tabIndex={0}
            onClick={(e) => {
                if (fromControl(e.target)) return
                showDetails((prev) => !prev)
            }}
            // Only when the CARD itself has focus.
            //
            // This listened on the whole subtree, so a space typed into the
            // decline reason was swallowed by `preventDefault` and toggled the
            // case instead of typing -- the space bar simply did not work in
            // those boxes. Enter had the same problem, and a click into an
            // input closed the case out from under whoever was filling it in.
            onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    showDetails((prev) => !prev)
                }
            }}
            // Plain until it is opened.
            //
            // The outline carried the verdict, which was useful when the row
            // also carried the words -- a colour and a label saying the same
            // thing. With the label moved into the case, a coloured border is
            // a claim with nothing to read it against, and ten of them down a
            // page is a queue that looks alarming before anybody has looked
            // at anything. It comes back while the case is open, where the
            // words are.
            className={twMerge(
                'mb-3 flex cursor-pointer flex-col rounded-lg border bg-white/[0.02] p-3 font-bold tracking-wide shadow-md shadow-black/30',
                details && face ? face.card : 'border-white/15 hover:border-white/25'
            )}
        >
            {/* One header, and the whole of it opens the row.
             *
             * It was three stacked strips -- name, then date, then a bar that
             * said REQUESTED and carried the amount -- and only the third was
             * clickable. The status word was the largest thing on a row whose
             * subject is a person and an amount, and the date, which is how
             * you tell two requests from the same player apart, was the
             * smallest.
             *
             * A div rather than a button: the name filters the queue and the
             * copy control writes the clipboard, and neither can live inside
             * a <button>. They stop the click from reaching this one. */}
            {/* Fixed columns, not a wrapping row.
                
                Laid out with flex-wrap, each card sized itself to its own
                content: a long name pushed the tags along, and the date and
                amount landed wherever there was room. Same tracks on every
                card and the queue reads down a column. */}
            <div className='grid grid-cols-[1.5rem,11rem,minmax(0,1fr),13.5rem,7rem] items-center gap-x-4 px-1 py-1'>
                <FontAwesomeIcon icon={faUser} className='text-white/30' />
                <span className='flex min-w-0 flex-row items-center gap-1.5'>
                    <span
                        className='truncate text-lg text-white hover:underline'
                        onClick={(e) => {
                            e.stopPropagation()
                            filterHistory()
                        }}
                        title='Show only this player'
                    >
                        {withdrawRequest.created_by}
                    </span>
                    <span onClick={(e) => e.stopPropagation()}>
                        <Copyable
                            value={withdrawRequest.created_by}
                            className='opacity-50 hover:opacity-100'
                        />
                    </span>
                </span>
                {/* Everything known about the account and the request, to the
                    right of whose request it is. */}
                {/* The row is whose request it is, for how much, and when.

                    The verdict, the integrity score and the first-withdrawal
                    badge moved into the case. They are readings ABOUT the
                    account, and reading them takes a moment that a row in a
                    list does not have -- the card's outline already carries
                    the verdict as colour, which is the part that works at
                    the speed somebody scans a queue at.

                    A flag somebody applied by hand stays: that is not a
                    reading, it is a person's decision about this account, and
                    it belongs where the name is. */}
                <span className='flex min-w-0 flex-row flex-wrap items-center gap-1'>
                    <WithdrawRequest.AccountFlags />
                    {payoutError && <FontAwesomeIcon icon={faWarning} className='text-red-500' />}
                </span>

                <span
                    className='whitespace-nowrap text-right font-normal text-xs text-white/60'
                    title={`Submitted ${when(withdrawRequest.created_at)}`}
                >
                    {when(withdrawRequest.created_at, false)}
                </span>
                <span className='flex flex-row justify-end text-lg'>
                    <WithdrawRequest.Amount />
                </span>
            </div>

                {/* Over the queue, not inside it.

                    Expanding in place left the decision surrounded by nine
                    other people's requests, and pushed the rows below it a
                    screen and a half down. A reviewer deciding whether to
                    move somebody's money should have that request and
                    nothing else in front of them.

                    The body swallows its own clicks: a click landing on a
                    fact tile must not fold the case away mid-decision. */}
                <CaseModal
                    open={details}
                    onClose={() => showDetails(false)}
                    title={`${withdrawRequest.amount} ${currencyLabel(withdrawRequest.currency)} to ${withdrawRequest.created_by}`}
                >
                    {/* The decision, before the evidence it was drawn from. */}
                    <small>
                        <WithdrawRequestCase />
                    </small>
                    <small>
                        <WithdrawRequest.TriageDetail />
                    </small>
                    <small>
                        {/* The request id and the destination address are for
                            support and for a chain lookup, not for the
                            decision. They are still in the payout error and
                            the audit where either is needed. */}
                        {/* Two labelled notes, not one "Comment" (D-108): the
                            decline used to overwrite the approver's note in
                            the same field, so nobody could tell which it was. */}
                        {withdrawRequest.approve_comment && (
                            <div className='p-2 text-white/50'>
                                Approver's note: {withdrawRequest.approve_comment}
                            </div>
                        )}
                        {withdrawRequest.decline_reason && (
                            <div className='p-2 text-white/50'>
                                Decline reason: {withdrawRequest.decline_reason}
                            </div>
                        )}

                        <div className='m-2 flex flex-row items-center rounded-lg border border-white/15 bg-white/[0.04] p-1'>
                            {approvable && <WithdrawRequest.ApproveButton />}
                            {approvable && <WithdrawRequestDeclineButton />}
                            {revokeable && <WithdrawRequest.RevokeApproveButton />}
                            {processable && <WithdrawRequestProcessButton />}
                            {not_finalized && payoutError && <WithdrawRequestReturnFailedButton />}
                            {/* {not_finalized && <WithdrawRequestCheckFinalizedButton />} */}
                        </div>
                    </small>
                    {payoutError && (
                        <div className='flex flex-col gap-1 p-2'>
                            <div className='text-red-500'>Payout error</div>
                            <div className='overflow-x-auto'>{payoutError}</div>
                        </div>
                    )}
                    {/* Why this account is flagged, if it is. Sits with the session
                        history because both answer the same question: is this payout
                        one we should be making. */}
                    <FraudDetail userId={withdrawRequest.user_id} />
                    {/* Six hundred rows of forensic evidence began directly
                        under the approve button, which made a routine payout
                        read as an investigation. It is the right material and
                        the wrong default: folded away, and one click for the
                        cases that need it. */}
                    <SessionHistoryFold
                        userId={withdrawRequest.user_id}
                        streaks={(withdrawRequest.triage?.signals ?? []).find(
                            (signal: any) => signal.key === 'repeat_victim' && !signal.ok
                        )}
                    />
                </CaseModal>
            {approve ? (
                <small
                    className='flex flex-row items-center gap-1 self-end'
                    title={`Approved ${when(approve.created_at)}`}
                >
                    <span className='font-normal text-white/60'>approved by</span>
                    <span className='text-white'>{approve.created_by}</span>
                </small>
            ) : (
                <small>&nbsp;</small>
            )}
        </div>
    )
}

/**
 * One request, over a blurred queue.
 *
 * Escape and a click on the backdrop both close it, because a reviewer who
 * has decided nothing should not have to find a button to leave. The panel
 * itself does not close on click -- everything in it is read or typed into.
 *
 * `overscroll-contain` so scrolling to the end of six hundred sessions does
 * not then start scrolling the queue behind it, and the body's own scroll is
 * locked while it is open for the same reason.
 */
const CaseModal = ({
    open,
    onClose,
    title,
    children,
}: {
    open: boolean
    onClose: () => void
    title: string
    children: React.ReactNode
}) => {
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [open, onClose])

    if (!open) return null

    return (
        <div
            role='dialog'
            aria-modal='true'
            aria-label={title}
            onClick={(e) => {
                e.stopPropagation()
                onClose()
            }}
            className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 backdrop-blur-sm sm:p-8'
        >
            {/* Outside the panel, against the backdrop. Inside it, the close
                sat over the case and read as part of the request rather than
                as the way out of it. */}
            <button
                type='button'
                onClick={onClose}
                title='Close (Esc)'
                className='fixed right-4 top-4 z-10 rounded-full border border-white/20 bg-black/60 px-3 py-1.5 text-white/60 hover:border-white/40 hover:text-white'
            >
                <FontAwesomeIcon icon={faXmark} />
            </button>
            <div
                onClick={(e) => e.stopPropagation()}
                // `my-auto` with `items-center`: centred when the case fits
                // on screen, and still scrollable from its own top when it
                // does not. `items-center` alone clips the top of anything
                // taller than the viewport, which is most cases once the
                // session history is open.
                className='relative my-auto w-full max-w-6xl cursor-default rounded-xl border border-white/20 bg-neutral-950 shadow-2xl shadow-black/60'
            >
                {children}
            </div>
        </div>
    )
}

/**
 * The kills traded with each account on the same machine.
 *
 * The headline says they share a machine; this says what they did with it.
 * Kills one way and not the other is the shape of feeding -- an account made
 * to lose to the one being paid -- and it cannot be seen from a total.
 *
 * There is no money column, and it is not an oversight. The ledger credits a
 * spawn-fee share to a balance; it does not record which kill it came from,
 * so a figure per opponent would have to be invented from an average. On a
 * screen somebody approves payments from, an invented number is worse than
 * an absent one.
 */
/**
 * The other accounts on this machine: who, how much they play, and the kills
 * that ran between them and the account being reviewed.
 *
 * Sessions matter more than the kill split here. Two accounts on one machine
 * is ordinary when the second has three matches on it -- a flatmate trying
 * the game once -- and is something else when it has four hundred.
 */
const MachineAccounts = ({
    rows,
    who,
    fresh,
}: {
    rows: Array<{ username: string; sessions: number; kills: number; deaths: number; killed_by_them?: number; killed_them?: number }>
    /** The account being reviewed, named in the last column's header. */
    who: string
    /** Accounts that appeared after this was dismissed, marked NEW. */
    fresh?: Set<string>
}) => (
    <div className='flex flex-col gap-0.5'>
        <div className='grid grid-cols-[minmax(0,1fr),6rem,5rem,5rem,10rem,10rem] gap-3 text-[10px] uppercase tracking-widest text-white/40'>
            <span>Account</span>
            <span className='text-right'>Matches</span>
            <span className='text-right'>Kills</span>
            <span className='text-right'>Deaths</span>
            <span className='text-right'>{who} killed them</span>
            <span className='text-right'>They killed {who}</span>
        </div>
        {rows.map((row) => (
            <div
                key={row.username}
                className='grid grid-cols-[minmax(0,1fr),6rem,5rem,5rem,10rem,10rem] items-center gap-3 border-t border-white/10 py-1'
            >
                <span className='flex min-w-0 flex-row items-center gap-1.5'>
                    <span className='truncate'>{row.username}</span>
                                {fresh?.has(row.username) && (
                                    <span
                                        title='Appeared after this was dismissed'
                                        className='rounded border border-amber-400/60 px-1 text-[9px] font-bold uppercase tracking-widest text-amber-300'
                                    >
                                        new
                                    </span>
                                )}
                    <a
                        href={`/team-tools/accounts/users/${row.username}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={(e) => e.stopPropagation()}
                        title={`Open ${row.username} in a new tab`}
                        className='rounded border border-white/15 px-1.5 text-[10px] text-white/40 hover:border-white/40 hover:text-white'
                    >
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                </span>
                <span className='text-right tabular-nums'>{row.sessions}</span>
                <span className='text-right tabular-nums'>{row.kills}</span>
                <span className='text-right tabular-nums'>{row.deaths}</span>
                <span className='text-right tabular-nums text-amber-300'>{row.killed_by_them ?? 0}</span>
                <span className='text-right tabular-nums text-amber-300'>{row.killed_them ?? 0}</span>
            </div>
        ))}
    </div>
)

const PairFold = ({
    rows,
    hwid,
    who,
    fresh,
}: {
    rows: Array<{ username: string; dealt: number; taken: number }>
    /** The machine they are all on, so the finding can be quoted elsewhere. */
    hwid?: string
    /** The account being reviewed, named in the kill headers. */
    who?: string
    /** Accounts that appeared after this was dismissed, marked NEW. */
    fresh?: Set<string>
}) => {
    const [open, setOpen] = useState(true)
    return (
        <div className='flex flex-col gap-1'>
            <button
                type='button'
                onClick={() => setOpen((v) => !v)}
                className='flex w-max flex-row items-center gap-2 text-[10px] uppercase tracking-widest text-white/50 hover:text-white'
            >
                <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
                {/* Named for who is in it. "Who killed whom" describes the
                    columns; the list is the victims that share a machine,
                    which is the finding. */}
                {open
                    ? 'Hide victims sharing the same machine ID'
                    : 'Show victims sharing the same machine ID'}
            </button>
            {open && (
                <div className='flex flex-col gap-0.5'>
                    <div className='grid grid-cols-[minmax(0,1fr),10rem,10rem] gap-3 text-[10px] uppercase tracking-widest text-white/40'>
                        <span>Account</span>
                        <span className='text-right'>{who ? `${who} killed them` : 'Kills'}</span>
                        <span className='text-right'>{who ? `They killed ${who}` : 'Deaths'}</span>
                    </div>
                    {rows.map((row) => (
                        <div
                            key={row.username}
                            className='grid grid-cols-[minmax(0,1fr),10rem,10rem] gap-3 border-t border-white/10 py-1'
                        >
                            <span className='flex min-w-0 flex-row items-center gap-1.5'>
                                <span className='truncate'>{row.username}</span>
                                {fresh?.has(row.username) && (
                                    <span
                                        title='Appeared after this was dismissed'
                                        className='rounded border border-amber-400/60 px-1 text-[9px] font-bold uppercase tracking-widest text-amber-300'
                                    >
                                        new
                                    </span>
                                )}
                                {/* Beside the name, not on it: the name is
                                    what is being read, and a link that IS the
                                    text makes somebody choose between reading
                                    it and clicking it. */}
                                <a
                                    href={`/team-tools/accounts/users/${row.username}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={(e) => e.stopPropagation()}
                                    title={`Open ${row.username} in a new tab`}
                                    className='rounded border border-white/15 px-1.5 text-[10px] text-white/40 hover:border-white/40 hover:text-white'
                                >
                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                </a>
                            </span>
                            <span className='text-right tabular-nums'>{row.dealt}</span>
                            <span className='text-right tabular-nums text-white/60'>{row.taken}</span>
                        </div>
                    ))}
                    {hwid && (
                        <span className='pt-1 font-mono text-[10px] text-white/40'>
                            Shared machine ID: {hwid}
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}

/** The play, on request rather than on arrival. Leads with the kill streaks
 *  behind a "killed X N times in a row" flag -- every run, when, and the
 *  matches it happened in -- so the flag can be reviewed where the play is. */
const SessionHistoryFold = ({ userId, streaks }: { userId: number; streaks?: any }) => {
    const [open, setOpen] = useState(false)
    return (
        <div className='flex flex-col p-2'>
            <button
                type='button'
                onClick={() => setOpen((v) => !v)}
                className='flex w-max flex-row items-center gap-2 text-[10px] uppercase tracking-widest text-white/40 hover:text-white'
            >
                <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
                {open ? 'Hide their play' : 'Look at their play'}
            </button>
            {open && streaks && (
                <div className='flex flex-col gap-1 pt-2'>
                    <span className='text-[10px] uppercase tracking-widest text-white/40'>
                        Kills on the same player, in a row
                    </span>
                    <SignalLog signal={streaks} userId={userId} expanded />
                </div>
            )}
            {open && <PlayPanel userId={userId} />}
        </div>
    )
}

WithdrawRequest.Amount = WithdrawRequestAmount
WithdrawRequest.ApproveButton = WithdrawRequestApproveButton
WithdrawRequest.RevokeApproveButton = WithdrawRequestRevokeApproveButton
WithdrawRequest.DeleteButton = WithdrawRequestDeleteButton
WithdrawRequest.DetailsContainer = WithdrawRequestDetailsContainer
WithdrawRequest.DetailsButton = WithdrawRequestDetailsButton
WithdrawRequest.Details = WithdrawRequestDetails
WithdrawRequest.Status = WithdrawRequestStatus
WithdrawRequest.Block = WithdrawRequestBlock
WithdrawRequest.Verdict = WithdrawRequestVerdict
WithdrawRequest.AccountFlags = WithdrawRequestAccountFlags
WithdrawRequest.Integrity = WithdrawRequestIntegrity
WithdrawRequest.History = WithdrawRequestHistory
WithdrawRequest.TriageHeadline = WithdrawRequestTriageHeadline
WithdrawRequest.TriageDetail = WithdrawRequestTriageDetail
WithdrawRequest.StatusForUser = WithdrawRequestStatusForUser
WithdrawRequest.AdminTemplate = WithdrawRequestAdminTemplate
WithdrawRequest.UserTemplate = WithdrawRequestUserTemplate

export { WithdrawRequest }
